/**
 * libros.ts — hablar con /api sobre catálogo, diario y mayor.
 *
 * Es el único sitio del frente que conoce las rutas y la forma de las
 * respuestas. Lo que sale de acá ya viene con los nombres del producto
 * (`nombreEn`, `permiteMovimiento`) y no con los de la base.
 *
 * ── El dinero, otra vez y por última vez ──────────────────────────────────
 *
 * Todos los importes son `string`. No hay un solo `Number(...)` en este
 * archivo, ni siquiera para comparar. Las respuestas se pasan tal cual a
 * `dinero.ts`, que es la única casa donde un importe se interpreta.
 *
 * ── Los errores tienen nombre ─────────────────────────────────────────────
 *
 * La API contesta 422 con un `codigo` (`SB021`, `SB023`…) y **sin mensaje**.
 * El texto que lee una persona vive en el diccionario, que es bilingüe. Traer
 * el mensaje de la base sería traer español a una pantalla en inglés.
 */

import type { LineaDeApertura } from '../nucleo/apertura.ts';

export interface CuentaReal {
  readonly codigo: string;
  readonly padre: string | null;
  readonly nombre: string;
  readonly nombreEn: string;
  readonly tipo: string;
  readonly niif18: string | null;
  readonly naturaleza: 'deudora' | 'acreedora';
  readonly permiteMovimiento: boolean;
  readonly activa: boolean;
  readonly dePlantilla: boolean;
  readonly moneda: string | null;
  readonly stream: string | null;
  /** Qué dimensiones exige esta cuenta: `customer`, `project`, `department`. */
  readonly clasificacion: readonly string[];
  readonly nota: string | null;
}

export interface LineaAsiento {
  readonly linea: number;
  readonly cuenta: string;
  readonly nombre: string;
  readonly nombreEn: string;
  readonly debe: string;
  readonly haber: string;
  /** La moneda de la línea tal como se escribió (`0122`). Cuando coincide con la
   *  de los libros, no hace falta reeenseñarla — el frente lo sabe. */
  readonly moneda: string;
  /** El tipo que aplicó `registrar_asiento`. `null` cuando la línea ya estaba en
   *  la moneda de los libros, o cuando no hubo que convertir. Viaja como texto
   *  (§6.1): un número de JavaScript con escala 6 pierde el último dígito. */
  readonly tipo_de_cambio: string | null;
  /** El importe en la moneda del PAPEL (`0122`). Lo divide la BASE —la aritmética
   *  del dinero no vive en el navegador—. Igual al funcional cuando no hubo
   *  conversión. */
  readonly debe_original: string;
  readonly haber_original: string;
  readonly detalle: string | null;
  readonly clasificacion: Readonly<Record<string, string>>;
}

export interface AsientoReal {
  readonly numero: string;
  /** `YYYY-MM-DD`. Texto, nunca un `Date`: un `Date` se corre de día por huso. */
  readonly fecha: string;
  readonly glosa: string;
  readonly origen: string;
  /**
   * Cuándo se TECLEÓ, que no es la fecha contable.
   *
   * Un asiento de marzo escrito en junio es legítimo y es información: en una
   * revisión, el segundo es el dato que se pregunta. La columna existía desde
   * E1 y no la leía nadie — una columna que existe hace creer que la
   * funcionalidad existe.
   */
  readonly registrado_en: string;
  readonly total: string;
  readonly lineas: readonly LineaAsiento[];

  /**
   * Quién lo escribió. Igual que `registrado_en`, la columna estaba desde E1
   * y no la leía nadie — con el agravante de que el pie de «Usuarios de la
   * empresa» la prometía en voz alta: «su nombre sigue en la bitácora y en
   * cada asiento suyo».
   *
   * ▶ El uuid viaja ADEMÁS del nombre, y no sobra.
   *
   * `creado_por === null` es «no lo escribió una persona» —una herramienta, la
   * siembra, la apertura—. `creado_por` con valor y `quien === null` es «la
   * persona existió y su fila se borró». Sin el uuid la pantalla no puede
   * separarlos, y acaba diciendo «usuario retirado» de alguien que nunca
   * existió: es lo que le pasó a la bitácora de la demo antes de la 0039.
   */
  readonly creado_por: string | null;
  readonly quien: string | null;
  readonly correo: string | null;
}

export interface MovimientoMayor {
  readonly asiento: string;
  readonly linea: number;
  readonly fecha: string;
  readonly glosa: string;
  readonly detalle: string | null;
  readonly debe: string;
  readonly haber: string;
  readonly saldo: string;
}

/** Una línea lista para mandar. Los importes ya en forma canónica. */
export interface LineaParaMandar {
  readonly cuenta: string;
  /** En qué moneda se escribió. La base la exige: `not null`. */
  readonly moneda?: string;
  readonly debe?: string;
  readonly haber?: string;
  readonly detalle?: string;
  readonly clasificacion?: Record<string, string>;
}

export type Pedir = (ruta: string, init?: RequestInit) => Promise<Response>;

/**
 * Lo que salió mal, con nombre.
 *
 * `codigo` es el `SB0xx` de la base cuando lo hay. `clase` distingue lo que la
 * pantalla trata distinto: una regla que dijo que no se corrige editando; un
 * fallo de red se corrige reintentando, y decir «no se pudo» para los dos
 * manda a la persona a buscar el problema en el sitio equivocado.
 */
export class FalloDeApi extends Error {
  readonly clase: 'regla' | 'permiso' | 'conexion' | 'servidor';
  readonly codigo: string | null;

  constructor(clase: FalloDeApi['clase'], codigo: string | null) {
    super(codigo ?? clase);
    this.name = 'FalloDeApi';
    this.clase = clase;
    this.codigo = codigo;
  }
}

async function leer<T>(r: Response): Promise<T> {
  if (r.ok) return await r.json() as T;

  const cuerpo = await r.json().catch(() => null) as { codigo?: string } | null;
  const codigo = typeof cuerpo?.codigo === 'string' ? cuerpo.codigo : null;

  if (r.status === 422) throw new FalloDeApi('regla', codigo);
  if (r.status === 401 || r.status === 403) throw new FalloDeApi('permiso', codigo);
  throw new FalloDeApi('servidor', codigo);
}

/** Envuelve un fallo de red para que no llegue como `TypeError: fetch failed`. */
async function pedirSeguro(pedir: Pedir, ruta: string, init?: RequestInit) {
  try {
    return await pedir(ruta, init);
  } catch {
    throw new FalloDeApi('conexion', null);
  }
}

/**
 * Qué valores admite cada dimensión CERRADA.
 *
 * Una dimensión que no aparece está abierta a propósito —`customer` y
 * `project`— y se pide con un campo libre. Ver `control.vocabulario_cerrado`.
 */
export type VocabularioAnalitico = Readonly<Record<string, readonly string[]>>;

export async function traerCatalogo(pedir: Pedir): Promise<CuentaReal[]> {
  const r = await pedirSeguro(pedir, '/api/cuentas');
  const cuerpo = await leer<{ cuentas: CuentaReal[] }>(r);
  return cuerpo.cuentas;
}

/**
 * Una dimensión que exige otra cuando toma cierto valor.
 *
 * `department = support` exige `modalidad`, porque el índice de mantenimiento
 * tiene dos metas y el gasto del área tiene que poder repartirse entre las dos.
 * El de ventas no la exige: ahí no significa nada, y un campo obligatorio sin
 * significado se rellena con cualquier cosa.
 *
 * Ver `control.clasificacion_condicional`. La regla vive en el catálogo; esto
 * solo la trae para que el formulario pueda pedir el campo ANTES de que el
 * disparador lo cobre con `SB023`.
 */
export interface CondicionDeClasificacion {
  readonly dimension: string;
  readonly valor: string;
  readonly exige: string;
}

/** El catálogo, el vocabulario y las condiciones: una sola pregunta. */
export async function traerCatalogoCompleto(
  pedir: Pedir,
): Promise<{
  cuentas: CuentaReal[];
  vocabulario: VocabularioAnalitico;
  condicional: readonly CondicionDeClasificacion[];
}> {
  const r = await pedirSeguro(pedir, '/api/cuentas');
  const cuerpo = await leer<{
    cuentas: CuentaReal[];
    vocabulario?: VocabularioAnalitico;
    condicional?: readonly CondicionDeClasificacion[];
  }>(r);
  return {
    cuentas: cuerpo.cuentas,
    vocabulario: cuerpo.vocabulario ?? {},
    // El mismo cinturón que el vocabulario: si esto no llega, el formulario
    // pide lo que la cuenta exige y el disparador cobra lo demás. Es peor que
    // con la lista, y mucho mejor que una pantalla que revienta al recorrer.
    condicional: Array.isArray(cuerpo.condicional) ? cuerpo.condicional : [],
  };
}

/**
 * Una lista que puede venir cortada, y lo dice.
 *
 * ▶ Se devuelve el sobre y no el array pelado A PROPÓSITO.
 *
 * `traerDiario` devolvía `AsientoReal[]`, y con eso quien llama no tiene dónde
 * enterarse de que faltan filas: le llegan doscientas y parecen todas. El tipo
 * obliga a mirar — para leer los asientos hay que escribir `.asientos`, y ahí
 * al lado está `hayMas`.
 */
export interface Recorte<T> {
  readonly filas: readonly T[];
  /** `true` si la base tenía más de las que se pidieron. */
  readonly hayMas: boolean;
  /** Cuántas se pidieron. «Hay más» sin número no ayuda a decidir qué filtrar. */
  readonly limite: number;
}

/**
 * El diario de un periodo.
 *
 * ▶ `anio` y `mes` dejaron de ser opcionales de adorno.
 *
 * Existían desde la 0012 y la pantalla los ignoraba: llamaba `traerDiario(pedir)`
 * y la base devolvía **la historia entera de la empresa con las líneas
 * anidadas** — 1,2 kB por asiento, sin techo. El filtro estaba en la base y en
 * el endpoint, y se moría acá.
 *
 * Se quedan opcionales porque el mayor de un año entero es una consulta
 * legítima, pero ahora hay un techo detrás: ver `control.diario`.
 */
export async function traerDiario(
  pedir: Pedir, anio?: number, mes?: number, limite?: number,
): Promise<Recorte<AsientoReal>> {
  const q = new URLSearchParams();
  if (anio) q.set('anio', String(anio));
  if (mes) q.set('mes', String(mes));
  if (limite) q.set('limite', String(limite));
  const cola = q.toString();
  const r = await pedirSeguro(pedir, `/api/asientos${cola ? `?${cola}` : ''}`);
  const cuerpo = await leer<{
    asientos: AsientoReal[]; hayMas?: boolean; limite?: number;
  }>(r);
  return {
    filas: cuerpo.asientos,
    hayMas: cuerpo.hayMas === true,
    limite: cuerpo.limite ?? cuerpo.asientos.length,
  };
}

/**
 * El mayor de una cuenta, en un periodo.
 *
 * El corte importa más acá que en el diario: la columna de saldo se acumula
 * desde la primera fila, así que **una lista truncada con saldo corrido parece
 * completa**. La base corta por el final para que las filas visibles lleven su
 * saldo verdadero; `hayMas` es lo que permite decirlo.
 */
export async function traerMayor(
  pedir: Pedir, cuenta: string, anio?: number, mes?: number, limite?: number,
): Promise<Recorte<MovimientoMayor>> {
  const q = new URLSearchParams({ cuenta });
  if (anio) q.set('anio', String(anio));
  if (mes) q.set('mes', String(mes));
  if (limite) q.set('limite', String(limite));
  const r = await pedirSeguro(pedir, `/api/mayor?${q.toString()}`);
  const cuerpo = await leer<{
    movimientos: MovimientoMayor[]; hayMas?: boolean; limite?: number;
  }>(r);
  return {
    filas: cuerpo.movimientos,
    hayMas: cuerpo.hayMas === true,
    limite: cuerpo.limite ?? cuerpo.movimientos.length,
  };
}

/**
 * Registrar un asiento.
 *
 * `fecha` es `YYYY-MM-DD` y se manda tal cual. No se construye un `Date` en
 * ningún punto: `new Date('2026-04-10').toISOString()` en un huso negativo da
 * el 9 de abril, y un asiento que cambia de día según dónde esté sentado quien
 * lo teclea no es un asiento.
 */
export async function registrarAsiento(
  pedir: Pedir,
  fecha: string,
  glosa: string,
  lineas: readonly LineaParaMandar[],
): Promise<string> {
  const r = await pedirSeguro(pedir, '/api/asientos', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ fecha, glosa, lineas }),
  });
  const cuerpo = await leer<{ numero: string }>(r);
  return cuerpo.numero;
}

/**
 * Corrige la fecha de un asiento MANUAL (`0124`).
 *
 * ▶ Solo acepta orígenes `manual`. Si vino de otro módulo —un pago, el banco,
 * un devengo— rebota con `SB065` y el `hint` dice dónde se corrige.
 *
 * ▶ Ambos períodos (el viejo y el nuevo) tienen que estar abiertos (`SB066`).
 * Un asiento en un mes cerrado no se mueve: se compensa con otro.
 *
 * ▶ El motivo es OBLIGATORIO: queda en la auditoría con el valor viejo y el
 * nuevo, para que una revisión pueda ver qué cambió y por qué.
 */
export async function corregirFechaAsiento(
  pedir: Pedir, numero: string | number, fecha: string, motivo: string,
): Promise<string> {
  const r = await pedirSeguro(pedir, '/api/asientos', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ numero: String(numero), fecha, motivo }),
  });
  const cuerpo = await leer<{ fecha: string }>(r);
  return cuerpo.fecha;
}

export async function agregarCuenta(
  pedir: Pedir,
  datos: { padre: string; codigo: string; nombre: string; nombreEn?: string; nota?: string },
): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/cuentas', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(datos),
  });
  await leer(r);
}

export async function renombrarCuenta(
  pedir: Pedir,
  datos: { codigo: string; nombre: string; nombreEn: string; nota?: string },
): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/cuentas', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(datos),
  });
  await leer(r);
}

export async function activarCuenta(
  pedir: Pedir, codigo: string, activa: boolean,
): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/cuentas', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ codigo, activa }),
  });
  await leer(r);
}

/* ═══════════════════════════════════════════════════════════════════════════
   El tipo de cambio del día
   ═════════════════════════════════════════════════════════════════════════ */

export interface TipoDelDia {
  readonly moneda: string;
  readonly fecha: string;
  /** Texto, siempre. Es el que se congela en el asiento. */
  readonly venta: string;
  readonly compra: string | null;
  readonly fuente: 'vigente_declarado' | 'copiado_del_anterior';
}

/**
 * El tipo de una moneda en una fecha, o `null` si no hay.
 *
 * `null` no es un fallo: es un día sin publicar. La pantalla lo dice y no
 * convierte esa línea — que es exactamente lo que hace la base si igual se
 * manda (SB050). Inventar el del día anterior acá sería copiar sin marcarlo.
 */
export async function traerTipoDeCambio(
  pedir: Pedir, moneda: string, fecha: string,
): Promise<TipoDelDia | null> {
  const q = new URLSearchParams({ moneda, fecha });
  const r = await pedirSeguro(pedir, `/api/cambio?${q.toString()}`);
  const cuerpo = await leer<{ tipo: TipoDelDia | null }>(r);
  return cuerpo.tipo;
}

export interface MonedaOperable {
  readonly moneda: string;
  /** La del libro. No se convierte, así que no necesita tipo de cambio. */
  readonly es_local: boolean;
  /** Cuántos tipos hay publicados. Cero en la local. */
  readonly tipos: number;
  /** De qué día es el último. `null` en la local. */
  readonly ultimo: string | null;
}

/**
 * En qué monedas puede operar esta empresa.
 *
 * ▶ NO es la lista ISO. Una moneda que existe pero no tiene tipo de cambio
 * publicado produce facturas que no se pueden registrar, y el aviso llegaría
 * el día de la factura — no el día en que se eligió la moneda del proveedor.
 * La regla la aplica `control.monedas_operables()` (0094) y no esta capa.
 */
export async function traerMonedasOperables(pedir: Pedir): Promise<readonly MonedaOperable[]> {
  const r = await pedirSeguro(pedir, '/api/cambio');
  const cuerpo = await leer<{ monedas: readonly MonedaOperable[] }>(r);
  return cuerpo.monedas;
}

export interface MonedaConocida {
  readonly moneda: string;
  readonly tipos: number;
  readonly ultimo: string | null;
  /** Si alguna empresa la lleva como moneda local. */
  readonly en_uso: boolean;
}

/**
 * Las monedas que el sistema CONOCE — otra pregunta que `traerMonedasOperables`.
 *
 * Aquélla contesta «en qué puede moverse esta empresa» y su primera fila es la
 * moneda local. Ésta contesta «cuáles conoce el sistema», que es lo que hace
 * falta para elegir la moneda LOCAL de una empresa: la de hoy no es una
 * respuesta, y la lista de convertibles tampoco.
 *
 * ▶ No necesita empresa en contexto, y es el único caso: no lee los libros de
 * nadie. La usa la consola del operador, que todavía no tiene una.
 */
export async function traerMonedasConocidas(pedir: Pedir): Promise<readonly MonedaConocida[]> {
  const r = await pedirSeguro(pedir, '/api/cambio?conocidas=1');
  const cuerpo = await leer<{ monedas: readonly MonedaConocida[] }>(r);
  return cuerpo.monedas;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Los estados financieros
   ═════════════════════════════════════════════════════════════════════════ */

export interface FilaComprobacion {
  readonly codigo: string;
  readonly nombre: string;
  readonly nombre_en: string;
  readonly grupo: string;
  readonly naturaleza: 'deudora' | 'acreedora';
  readonly debe: string;
  readonly haber: string;
  readonly saldo: string;
}

export interface FilaResultadoReal {
  readonly codigo: string;
  readonly nombre: string;
  readonly nombre_en: string;
  readonly grupo: string;
  readonly stream: string | null;
  readonly mes: string;
  readonly acumulado: string;
}

export interface FilaBalance {
  readonly codigo: string;
  readonly nombre: string;
  readonly nombre_en: string;
  readonly grupo: string;
  readonly saldo: string;
  readonly es_resultado: boolean;
  /**
   * La sección del balance: `11` activo corriente, `12` no corriente, y sus
   * pares del pasivo. El patrimonio no se subdivide y trae su grupo.
   *
   * ▶ Sale del ÁRBOL del catálogo —`control.secciones_del_balance()`— y no del
   * prefijo del código. `null` si la cuenta no cuelga de ninguna sección
   * conocida, y entonces la pantalla la pone aparte en vez de esconderla.
   */
  readonly seccion: string | null;
  readonly seccion_nombre: string | null;
  readonly seccion_nombre_en: string | null;
  /**
   * Esta línea lleva una RECLASIFICACIÓN DE PRESENTACIÓN, así que su saldo NO es
   * el del mayor.
   *
   * ▶▶ Dos cosas se presentan donde van sin asentarse: el pasivo de contrato que
   * vence a más de doce meses del corte, y el activo de contrato que quedó en
   * negativo —que no es un activo que valga menos: es un pasivo—. Las dos
   * dependen de LA FECHA DEL CORTE, y un asiento las fijaría a un instante.
   *
   * ▶ Hace falta decirlo en la pantalla. Quien concilie el balance contra el
   * mayor encontraría una cuenta con un saldo que ningún asiento produjo, y
   * buscaría el asiento que falta. *Un número que no está en el libro y no avisa
   * de que no está es peor que el número que falta.*
   */
  readonly reclasificado: boolean;
}

/** Una casilla de la declaración de RENTA. La 50 es la conciliación. */
export interface FilaD100 {
  readonly casilla: string;
  readonly concepto: string;
  readonly concepto_en: string;
  readonly importe: string;
  readonly es_total: boolean;
  readonly moneda: string;
}

/**
 * Una cuenta debajo de una casilla del D-100.
 *
 * ▶ Existe porque la declaracion se presenta JURADA. La casilla 31 dice
 * «Gastos operativos deducibles · ₡X» y hasta hoy no habia forma de abrirla:
 * ni en la pantalla, ni en el CSV, ni preguntandole a la base. Un numero que no
 * se puede abrir no se puede defender — ni antes de teclearlo en ATV, ni
 * despues si alguien pregunta de donde salio.
 *
 * La casilla 50 NO aparece aca: es la conciliacion y no tiene cuentas debajo.
 */
export interface FilaD100Desglose {
  readonly casilla: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly nombre_en: string;
  readonly importe: string;
  readonly moneda: string;
}

/**
 * Una linea de la conciliacion del IVA del mes.
 *
 * ▶ Del MES, no acumulada: la renta es anual y el IVA es mensual. Doce
 * declaraciones por cada una.
 *
 * Y no lleva casilla a proposito. Es una CONCILIACION, no el formulario D-104:
 * poner numeros de casilla exigiria copiar la numeracion oficial, e inventarla
 * seria el mismo defecto que el XML para ATV que se retiro el 7 de setiembre.
 */
export interface FilaIva {
  readonly concepto: string;
  readonly concepto_en: string;
  readonly importe: string;
  readonly es_total: boolean;
  readonly moneda: string;
}

/** Que cuentas forman el debito o el credito fiscal del mes. */
export interface FilaIvaDesglose {
  readonly papel: 'debito' | 'credito';
  readonly codigo: string;
  readonly nombre: string;
  readonly nombre_en: string;
  readonly importe: string;
  readonly moneda: string;
}

export interface FilaPresentacion {
  readonly codigo: string;
  readonly nombre: string;
  readonly grupo: string;
  readonly saldo_local: string;
  readonly tipo_aplicado: string;
  readonly criterio: 'cierre' | 'transaccion' | 'historico';
  readonly saldo_presentacion: string;
}

export interface Cuadre {
  readonly comprobacion_debe: string;
  readonly comprobacion_haber: string;
  readonly comprobacion_cuadra: boolean;
  readonly activo: string;
  readonly pasivo_y_patrimonio: string;
  readonly balance_cuadra: boolean;
}

/**
 * Una línea del corte por contraparte.
 *
 * ▶ `contraparte` es el CLIENTE en «cobrar» y el PROVEEDOR en «pagar». Se
 * llamaba `cliente` y en el lado de pagar traía un proveedor: la pantalla ya
 * rotulaba «Contraparte» ahí, o sea que el dato tenía un nombre y lo que se veía
 * tenía otro. Cuál dimensión es la lee la base del catálogo (`0074`).
 *
 * ▶ Puede ser `null`, y eso NO es «sin nombre»: es un saldo que entró por el
 * asiento de apertura, que va sin dimensiones a propósito (`0041`) porque el
 * balance del sistema anterior trae una cifra por cuenta y el desglose se quedó
 * allá. La pantalla lo dice; agruparlo bajo una etiqueta lo convertiría en una
 * contraparte más, para siempre.
 *
 * Y `a_favor` va aparte del total a propósito: restarlo daría una cifra que no
 * se le puede cobrar a nadie.
 */
/**
 * Una fila del estado de cambios en el patrimonio.
 *
 * ▶ Tres columnas y no una matriz de eventos. Un estado completo separa
 * aportes, distribuciones y traslados a reservas, y eso exige mirar la
 * CONTRAPARTIDA de cada línea. Acá cada componente dice cuánto traía, cuánto se
 * movió y cuánto quedó — la mitad que no exige inventar una clasificación. Ver
 * la cabecera de la `0099`.
 */
/**
 * Una línea del estado de flujos de efectivo.
 *
 * ▶ Son LÍNEAS, no subtotales: los subtotales los suma la pantalla con
 * `sumar()`. Las dos de `saldos` —el efectivo al inicio y al cierre— sí vienen
 * de la base y no son sumas: salen de las cuentas de efectivo, que es el otro
 * lado de la igualdad con la que este estado se comprueba a sí mismo.
 */
export interface FilaFlujo {
  /** `operacion`, `inversion`, `financiacion`, `fx`, `sin_clasificar`, `saldos`. */
  readonly seccion: string;
  /** El código de la cuenta, o un nombre propio: `RESULTADO`, `FINAL`… */
  readonly codigo: string;
  readonly nombre: string;
  readonly nombre_en: string;
  readonly monto: string;
  /** Un saldo de efectivo, que NO entra en ningún subtotal de actividad. */
  readonly es_saldo: boolean;
  /** El orden en que se presenta. La base ya las manda ordenadas. */
  readonly orden: number;
}

export interface FilaPatrimonio {
  readonly componente: string;
  readonly nombre: string;
  readonly nombre_en: string;
  /** Lo que traía al empezar el ejercicio. La apertura NO es un movimiento. */
  readonly saldo_inicial: string;
  readonly movimiento: string;
  readonly saldo_final: string;
  /** El resultado del periodo, que todavía no tiene cuenta propia. */
  readonly es_resultado: boolean;
}

export interface FilaAntiguedad {
  /** El CÓDIGO: lo que identifica y lo que viaja dentro de cada asiento. */
  readonly contraparte: string | null;
  /**
   * El nombre de la ficha, para leer. `null` cuando no hay ficha —la apertura
   * no la tiene— en vez de repetir el código: repetirlo escondería que ahí no
   * hay nadie dado de alta.
   */
  readonly nombre: string | null;
  /**
   * Los tramos cuentan desde el VENCIMIENTO desde la `0086`, y por eso cambian
   * de nombre: dejar `tramo_0_30` valiendo otra cosa habría sido la misma
   * columna, el mismo nombre y otro significado.
   */
  readonly corriente: string;
  readonly tramo_1_30: string;
  readonly tramo_31_60: string;
  readonly tramo_61_90: string;
  readonly tramo_mas_90: string;
  /**
   * Lo que no se puede envejecer: asentado a mano, la apertura, o un papel que
   * no decía vencimiento. Va aparte en vez de derivarse del plazo de crédito
   * —eso haría que un tramo significara dos cosas—. Misma forma que `a_favor`.
   */
  readonly sin_vencimiento: string;
  readonly total: string;
  readonly a_favor: string;
  readonly documentos: number;
  /** Días del documento MÁS VENCIDO. Cero si nada venció. */
  readonly mas_vencido: number;
  readonly moneda: string;
  /**
   * Cuánto de esta fila lo colocó el FIFO porque nadie dijo a qué iba (0082).
   *
   * ▶ En cero, el corte de esa contraparte es un HECHO. Con algo, esa parte es
   * una suposición — y decir cuánta es lo que separa un informe de una
   * afirmación. Ver la cabecera de la `0067`.
   */
  readonly sin_aplicar: string;
  /**
   * El saldo de las cuentas de esta contraparte, con su signo natural.
   *
   * ▶ Es contra ESTE número que `total` menos `a_favor` tiene que dar, y por
   * eso viaja: la pantalla afirmaba que los tramos daban el saldo de la cuenta
   * y no tenía el saldo para mostrarlo. Lo pidió la revisión del 24 de
   * setiembre con la diferencia medida — ₡ 11 719 130 contra ₡ 5 321 810.
   */
  readonly saldo_contable: string;
}

/** Un documento que sigue abierto, después de aplicar los abonos al más viejo. */
export interface FilaAntiguedadDetalle {
  readonly contraparte: string | null;
  readonly cuenta: string;
  readonly asiento: number;
  readonly linea: number;
  readonly fecha: string;
  readonly glosa: string;
  readonly detalle: string | null;
  /** Lo que el documento valía. */
  readonly importe: string;
  /** Lo que queda por cobrar o pagar de él. */
  readonly abierto: string;
  readonly dias: number;
  readonly moneda: string;
  /** Lo que se cerró porque alguien lo DIJO, no porque el FIFO lo colocara. */
  readonly aplicado: string;
  /** `null` no es un hueco: es que esa línea no viene de un papel que lo dijera. */
  readonly vence: string | null;
  /** Negativo si todavía falta; `null` cuando no hay vencimiento. */
  readonly dias_vencido: number | null;
}

/**
 * Los catorce estados del mes, en una sola conexión.
 *
 * ▶ Nace del defecto del 1 de octubre: la pantalla de Reportes disparaba
 * catorce `/api/estados` en paralelo y una se caía por saturación del pooler
 * de Supabase (15 sesiones máximo). Un solo endpoint abre una sola sesión y
 * pipelinea los catorce `select` sobre el mismo socket.
 */
export interface ReportesDelMes {
  readonly anio: number;
  readonly mes: number;
  readonly comprobacion: readonly FilaComprobacion[];
  readonly resultado: readonly FilaResultadoReal[];
  readonly balance: readonly FilaBalance[];
  readonly balance_previo: readonly FilaBalance[];
  readonly d100: readonly FilaD100[];
  readonly d100_desglose: readonly FilaD100Desglose[];
  readonly iva: readonly FilaIva[];
  readonly iva_desglose: readonly FilaIvaDesglose[];
  readonly cuadre: readonly Cuadre[];
  readonly patrimonio: readonly FilaPatrimonio[];
  readonly flujos: readonly FilaFlujo[];
  readonly antiguedad_cobrar: readonly FilaAntiguedad[];
  readonly antiguedad_cobrar_detalle: readonly FilaAntiguedadDetalle[];
  readonly antiguedad_pagar: readonly FilaAntiguedad[];
  readonly antiguedad_pagar_detalle: readonly FilaAntiguedadDetalle[];
  readonly panel_salud: readonly FilaPanelSalud[];
}

export async function traerReportesDelMes(
  pedir: Pedir, anio: number, mes: number,
): Promise<ReportesDelMes> {
  const q = new URLSearchParams({ anio: String(anio), mes: String(mes) });
  const r = await pedirSeguro(pedir, `/api/reportes-del-mes?${q.toString()}`);
  return leer<ReportesDelMes>(r);
}

/**
 * Panel de salud financiera: resultado agrupado por dimensión.
 *
 * Las filas vienen de `control.panel_de_salud` y se agrupan por `seccion`:
 *
 *   `ingresos`      · `clave` es el stream (`subscription`, `ps`, …)
 *   `cogs`          · `clave` es el stream (apareado con el ingreso)
 *   `opex`          · `clave` es el department (`ga`, `rnd`, …) o `sin_depto`
 *   `depreciacion`  · `clave` es el código de cuenta y lleva `nombre`
 *   `no_operativo`  · `clave` es el código de cuenta y lleva `nombre`
 *
 * Los importes vienen POSITIVOS para ingresos, cogs, opex y depreciación. La
 * sección `no_operativo` lleva signo (positivo si suma a la utilidad). Las
 * sumas y restas (NR, EBIT, EBITDA) se hacen del lado del frente.
 */
export interface FilaPanelSalud {
  readonly seccion: 'ingresos' | 'cogs' | 'opex' | 'depreciacion' | 'no_operativo';
  readonly clave: string;
  readonly nombre: string | null;
  readonly nombre_en: string | null;
  readonly mes: string;
  readonly ytd: string;
}

/**
 * El panel de salud viaja DENTRO de `traerReportesDelMes` para no abrir una
 * segunda sesión del pooler — igual que el balance comparativo. Si el día
 * de mañana una pantalla pide refrescar sólo este panel, se le vuelve a
 * dar un endpoint propio; mientras tanto no vive uno fantasma esperando.
 */

/**
 * Descargar un estado como CSV.
 *
 * ▶ Se pide con `fetch` y se entrega como blob, NO con un enlace de descarga.
 *
 * Un `<a href="/api/exportar" download>` es una navegación del navegador y **no
 * lleva la cabecera de autorización**: daría 401. La salida fácil —poner el
 * token en la URL— no se toma: una URL se guarda en el historial, en los
 * registros del servidor y en el «copiar dirección» de cualquiera.
 *
 * El precio es que el archivo pasa por memoria antes de guardarse. Para un
 * estado financiero de un mes eso son unos kilobytes.
 */
export type FormatoDeDescarga = 'csv' | 'xlsx';

export async function descargarEstado(
  pedir: Pedir, tipo: string, anio: number, mes: number,
  moneda?: string, formato: FormatoDeDescarga = 'csv',
): Promise<{ nombre: string; blob: Blob }> {
  const q = new URLSearchParams({ tipo, anio: String(anio), mes: String(mes) });
  if (moneda) q.set('moneda', moneda);
  if (formato !== 'csv') q.set('formato', formato);

  const r = await pedirSeguro(pedir, `/api/exportar?${q.toString()}`);
  if (!r.ok) {
    // El cuerpo de un error sí es JSON; el de un acierto es el archivo.
    const cuerpo = await r.json().catch(() => null) as { codigo?: string } | null;
    const codigo = typeof cuerpo?.codigo === 'string' ? cuerpo.codigo : null;
    if (r.status === 401 || r.status === 403) throw new FalloDeApi('permiso', codigo);
    if (r.status === 422) throw new FalloDeApi('regla', codigo);
    throw new FalloDeApi('servidor', codigo);
  }

  // ▶ La extensión del archivo debe reflejar el formato: un .csv con contenido
  //   de Excel abre feo, y un .xls con filas CSV peor. El backend ya pone el
  //   `content-disposition`, pero el navegador no lo respeta en todos los
  //   casos — así que el nombre que viaja al disco se arma del lado nuestro.
  const ext = formato === 'xlsx' ? 'xls' : 'csv';
  return {
    nombre: `${tipo}-${anio}-${String(mes).padStart(2, '0')}.${ext}`,
    blob: await r.blob(),
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   Los índices operativos
   ═════════════════════════════════════════════════════════════════════════ */

export interface IndiceReal {
  readonly clave: string;
  readonly nombre: string;
  readonly nombre_en: string;
  /** `indice`: más alto es mejor. `porcentaje_de_nr`: más bajo es mejor. */
  readonly tipo: 'indice' | 'porcentaje_de_nr';
  /** Texto, o `null` cuando no se pudo calcular. Nunca cero por defecto. */
  readonly valor: string | null;
  /** `null` significa META NO FIJADA, no meta de cero. */
  readonly meta: string | null;
  readonly numerador: string | null;
  readonly denominador: string | null;
  readonly estado: 'calculado' | 'sin_datos';
  /**
   * Por qué no se pudo calcular, como CLAVE (`sin_modalidad`,
   * `sin_ingreso_neto`, `sin_gasto_de_area`). La frase la arma la pantalla.
   */
  readonly motivo: string | null;
  /** El mes todavía no cerró: no se presentan como definitivos. */
  readonly provisional: boolean;
  /**
   * Alguien de ESTA empresa miró la meta y la hizo suya.
   *
   * ▶ Falso significa que sigue siendo la del modelo contable de la casa — y
   * enseñarla sin decirlo es lo que el §13 de la revisión llamó «un dato que se
   * ve como propio y es de todos». En un mes cerrado es lo que valía ENTONCES.
   */
  readonly meta_confirmada: boolean | null;
}

/** La meta de un índice, con la del modelo al lado para poder compararlas. */
export interface MetaDeIndice {
  readonly clave: string;
  readonly nombre: string;
  readonly nombre_en: string;
  readonly tipo: 'indice' | 'porcentaje_de_nr';
  readonly meta: string | null;
  /** La del modelo contable. Sin ella, «volver a la de la casa» no existe. */
  readonly meta_del_modelo: string | null;
  readonly confirmada: boolean;
}

export async function traerMetas(pedir: Pedir): Promise<MetaDeIndice[]> {
  const r = await pedirSeguro(pedir, '/api/indices?metas=1');
  const cuerpo = await leer<{ metas: MetaDeIndice[] }>(r);
  return cuerpo.metas;
}

/**
 * Declarar la meta de un índice — y con eso deja de ser la del modelo.
 *
 * ▶ `null` se admite y no es lo mismo que no declararla: dice que alguien miró
 * y decidió no fijarla todavía, que es lo que el modelo pide para suscripciones
 * —«con dos trimestres de datos reales en lugar de copiar un número de otro
 * modelo de negocio»—.
 */
export async function declararMeta(
  pedir: Pedir, clave: string, meta: string | null,
): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/indices', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ clave, meta }),
  });
  await leer(r);
}

export async function traerIndices(
  pedir: Pedir, anio: number, mes: number,
): Promise<IndiceReal[]> {
  const q = new URLSearchParams({ anio: String(anio), mes: String(mes) });
  const r = await pedirSeguro(pedir, `/api/indices?${q.toString()}`);
  const cuerpo = await leer<{ indices: IndiceReal[] }>(r);
  return cuerpo.indices;
}

/* ═══════════════════════════════════════════════════════════════════════════
   El impuesto de renta diferido — NIC 12 (0113)

   ▶▶ Las bases FISCALES no las conoce nadie más que quien hace la declaración:
   dependen de la depreciación admitida, de qué gastos son deducibles y de
   criterios que cambian con la ley. Deducirlas del libro sería inventarlas, así
   que se declaran concepto por concepto.

   ▶ Y el signo sale de la NATURALEZA de la partida, no de cuál base es mayor:
   en un activo el contable mayor produce un PASIVO diferido, y en un pasivo
   produce un ACTIVO. Las dos filas del medio de esa tabla son las que se
   voltean solas.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Una diferencia temporaria declarada, con su efecto a la tasa vigente. */
export interface DiferenciaTemporaria {
  /** `OBJETIVO` y `AJUSTE` son filas de resumen, no conceptos. */
  readonly concepto: string;
  readonly naturaleza: 'activo' | 'pasivo' | null;
  readonly base_contable: string | null;
  readonly base_fiscal: string | null;
  readonly diferencia: string | null;
  /** Positivo es pasivo diferido; negativo, activo diferido. */
  readonly imponible: string | null;
  /** `null` cuando la tasa de renta no está declarada: no se puede calcular. */
  readonly efecto: string | null;
  readonly nota: string | null;
  readonly orden: number;
}

export async function declararDiferencia(
  pedir: Pedir,
  datos: { anio: number; concepto: string; naturaleza: 'activo' | 'pasivo';
           contable: string; fiscal: string; nota: string | null },
): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/provisiones', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...datos, accion: 'diferencia' }),
  });
  await leer(r);
}

export async function eliminarDiferencia(
  pedir: Pedir, anio: number, concepto: string,
): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/provisiones', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ anio, concepto, accion: 'eliminar_diferencia' }),
  });
  await leer(r);
}


/* ═══════════════════════════════════════════════════════════════════════════
   La consola de operador
   ═════════════════════════════════════════════════════════════════════════ */

export interface EmpresaDeOperador {
  readonly codigo: string;
  readonly razon_social: string;
  readonly identificacion: string | null;
  /** La cédula es un relleno. Se ve en la lista, no escondida en un detalle. */
  readonly provisional: boolean;
  readonly moneda_local: string;
  readonly estado: 'activa' | 'suspendida' | 'cerrada';
  readonly es_demo: boolean;
  readonly miembros: number;
  readonly creada_en: string;
}

export interface MiembroDeEmpresa {
  readonly correo: string;
  readonly nombre: string;
  /**
   * Los tres que la base acepta, no un `string`.
   *
   * Con `string`, la pantalla no puede armar la clave del diccionario a partir
   * del valor sin un casteo — y un casteo ahí habría tapado justo lo que se
   * quería comprobar: que la clave exista. Con el tipo estrecho lo comprueba
   * el compilador.
   */
  readonly rol: 'admin' | 'contador' | 'solo_lectura';
  /** Ya entró alguna vez. Una invitación sin usar se ve igual si no se dice. */
  readonly entro: boolean;
}

/**
 * Un trabajo programado y si sigue corriendo.
 *
 * `estado` sale de comparar la última corrida contra la ventana de la tarea, y
 * se calcula AL LEER: un proceso muerto no se denuncia a sí mismo.
 */
export interface EstadoDeTarea {
  readonly clave: string;
  /** Dónde ir a mirar cuando diga «muda». Un nombre sin sitio no lleva a nada. */
  readonly disparador: string;
  readonly estado: 'nunca' | 'muda' | 'al_dia' | 'sin_fuente';
  readonly ultima: string | null;
  readonly minutos: number | null;
}

export async function traerEmpresasDelOperador(
  pedir: Pedir,
): Promise<{
  empresas: EmpresaDeOperador[];
  tareas: EstadoDeTarea[];
  emisores: EmisorDeCliente[];
}> {
  const r = await pedirSeguro(pedir, '/api/operador');
  return leer(r);
}

/** Una casilla de la puesta en marcha, y de quién depende marcarla. */
export interface PasoDePuestaEnMarcha {
  readonly paso: string;
  readonly cumplido: boolean;
  /**
   * `operador` o `contador`. Que venga de la base y no de la pantalla es lo
   * que permite decir «esto le toca al cliente» en vez de dejar al operador
   * esperando un botón que no existe.
   */
  readonly quien: 'operador' | 'contador';
  /**
   * El VALOR del paso —la identificación, cuántos accesos hay—, no una frase.
   * La base no manda texto que lea una persona: sería español en una pantalla
   * en inglés, y no habría dónde traducirlo.
   */
  readonly dato: string | null;
}

/**
 * Los accesos y la lista de puesta en marcha, en UNA petición.
 *
 * Van juntos porque la consola los dibuja juntos: en dos peticiones podrían
 * llegar de momentos distintos y enseñar una contradicción —cero accesos con
 * la casilla del primer acceso marcada— que nadie puede resolver mirando.
 */
export async function traerMiembros(
  pedir: Pedir, codigo: string,
): Promise<{ miembros: MiembroDeEmpresa[]; pasos: PasoDePuestaEnMarcha[] }> {
  const q = new URLSearchParams({ empresa: codigo });
  const r = await pedirSeguro(pedir, `/api/operador?${q.toString()}`);
  return leer<{ miembros: MiembroDeEmpresa[]; pasos: PasoDePuestaEnMarcha[] }>(r);
}

export async function crearEmpresa(
  pedir: Pedir,
  datos: {
    codigo: string; razonSocial: string; identificacion: string;
    // Las cuatro obligatorias, no opcionales: cambios 76 y 79. Un `?` acá deja
    // que la pantalla no lo diga, y el que no dice queda afirmando algo — que
    // es cliente, que la cédula es real, que es una sociedad, que lleva
    // colones. `tipo` y `moneda` eran opcionales y la consola no los
    // preguntaba: nadie los eligió nunca.
    esDemo: boolean; provisional: boolean; tipo: string; moneda: string;
  },
): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/operador', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ accion: 'crear_empresa', ...datos }),
  });
  await leer(r);
}

/**
 * Lo que pasó al dar el acceso: la membresía SIEMPRE, la invitación si se pudo.
 *
 * Son dos hechos y se devuelven separados a propósito. La membresía es lo que
 * decide si la persona puede entrar; la invitación es solo cómo se entera. Si
 * la segunda falla, la primera se queda — y la consola dice que hay que
 * invitar a mano, que es lo que se hacía antes de que esto existiera.
 */
export interface ResultadoDeAcceso {
  readonly ok: boolean;
  readonly invitado: boolean;
  /** `sin_llave`, `ya_existe` o `rechazada`. `null` si la invitación salió. */
  readonly motivo: string | null;
}

export async function darMembresia(
  pedir: Pedir,
  // `rol` obligatorio y sin valor por omisión: ver el comentario de
  // `dar_membresia` en 0033. Es acceso de escritura a los libros.
  datos: { codigo: string; correo: string; nombre: string; rol: string },
): Promise<ResultadoDeAcceso> {
  const r = await pedirSeguro(pedir, '/api/operador', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ accion: 'dar_membresia', ...datos }),
  });
  return leer<ResultadoDeAcceso>(r);
}

export async function quitarMembresia(
  pedir: Pedir, datos: { codigo: string; correo: string },
): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/operador', {
    method: 'DELETE',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(datos),
  });
  await leer(r);
}

/* ═════════════════════════════════════════════════════════════════════════
   La gente de la empresa — del lado del INQUILINO
   ═════════════════════════════════════════════════════════════════════════

   Lo de arriba es la consola: exige ser operador y recibe el código de la
   empresa. Esto es la empresa administrándose sola, y la empresa sale de la
   sesión. Dos puertas para dos personas distintas. */

export interface MiembroReal {
  readonly correo: string;
  readonly nombre: string;
  readonly rol: 'admin' | 'contador' | 'solo_lectura';
  readonly desde: string;
  /** Si ya entró alguna vez. Distingue una invitación perdida de una aceptada. */
  readonly ligado: boolean;
  readonly soy_yo: boolean;
}

/**
 * Los de MI empresa. Se llama distinto que la `traerMiembros` de arriba —la de
 * la consola, que recibe el código— porque son dos puertas distintas y llamar
 * a las dos igual invita a usar la que no toca.
 */
export async function traerMiembrosDeMiEmpresa(pedir: Pedir): Promise<MiembroReal[]> {
  const r = await pedirSeguro(pedir, '/api/miembros');
  const cuerpo = await leer<{ miembros: MiembroReal[] }>(r);
  return cuerpo.miembros;
}

export async function invitarAMiEmpresa(
  pedir: Pedir,
  datos: { correo: string; nombre: string; rol: string },
): Promise<ResultadoDeAcceso> {
  const r = await pedirSeguro(pedir, '/api/miembros', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(datos),
  });
  return leer<ResultadoDeAcceso>(r);
}

export async function cambiarRol(
  pedir: Pedir, datos: { correo: string; rol: string },
): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/miembros', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(datos),
  });
  await leer(r);
}

export async function sacarDeMiEmpresa(pedir: Pedir, correo: string): Promise<void> {
  // El correo va en la consulta: un DELETE con cuerpo lo tratan distinto los
  // intermediarios, y `fetch` no siempre lo manda.
  const r = await pedirSeguro(
    pedir, `/api/miembros?correo=${encodeURIComponent(correo)}`, { method: 'DELETE' });
  await leer(r);
}

/* ═════════════════════════════════════════════════════════════════════════
   Los saldos de apertura
   ═════════════════════════════════════════════════════════════════════════

   Del lado del INQUILINO y no de la consola: cargar una apertura es escribir
   en los libros, y un operador no tiene empresa en contexto. Es la mitad de
   la puesta en marcha que hace el contador. */

/**
 * Qué hacer con las cuentas de resultado del balance ajeno.
 *
 *   `limpio`       · se netean los grupos 4 y 5 contra utilidades acumuladas
 *   `mitad_de_ano` · entran tal cual, y el año en curso queda comparable
 *
 * No hay un tercer valor ni un defecto: lo elige el cliente, porque las dos
 * respuestas son correctas y la que corresponde depende de algo que solo él
 * sabe —si va a seguir declarando el año en el sistema anterior o no—.
 */
export type ModoDeApertura = 'limpio' | 'mitad_de_ano';

/** Una correspondencia: su código, su nombre y la cuenta nuestra. */
export interface FilaDeMapeo {
  readonly externo: string;
  readonly nombre_externo: string | null;
  readonly cuenta: string;
}

export async function traerMapeo(pedir: Pedir): Promise<FilaDeMapeo[]> {
  const r = await pedirSeguro(pedir, '/api/mapeo');
  const cuerpo = await leer<{ mapeo: FilaDeMapeo[] }>(r);
  return cuerpo.mapeo;
}

/** Reemplaza el mapeo entero. `PUT` por eso: ver la cabecera del endpoint. */
export async function guardarMapeo(
  pedir: Pedir,
  mapeo: ReadonlyArray<{ externo: string; nombre: string; cuenta: string }>,
): Promise<number> {
  const r = await pedirSeguro(pedir, '/api/mapeo', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ mapeo }),
  });
  const cuerpo = await leer<{ guardadas: number }>(r);
  return cuerpo.guardadas;
}

/**
 * Los pasos de MI empresa. La del operador es `traerMiembros(pedir, codigo)`,
 * que recibe el código; ésta no recibe nada.
 */
export async function traerMiPuestaEnMarcha(pedir: Pedir): Promise<PasoDePuestaEnMarcha[]> {
  const r = await pedirSeguro(pedir, '/api/puesta-en-marcha');
  const cuerpo = await leer<{ pasos: PasoDePuestaEnMarcha[] }>(r);
  return cuerpo.pasos;
}

export interface EstadoDeLaApertura {
  readonly cargada: boolean;
  /**
   * Fecha desde la que la empresa lleva sus libros acá, cuando NO hay saldos
   * anteriores. `null` NO significa que los haya: significa que nadie lo ha
   * declarado. Son dos hechos distintos y por eso no es un booleano.
   */
  readonly sinSaldosPrevios?: string | null;
  /** La moneda local de la empresa. Hace falta para anticipar el cuadre. */
  readonly moneda: string | null;
}

export async function traerEstadoDeLaApertura(pedir: Pedir): Promise<EstadoDeLaApertura> {
  const r = await pedirSeguro(pedir, '/api/apertura');
  return leer<EstadoDeLaApertura>(r);
}

/**
 * Carga los saldos. Los importes viajan como TEXTO tal como salieron del CSV:
 * esta función no los suma ni los redondea ni los mira.
 */
export async function cargarApertura(
  pedir: Pedir,
  datos: { fecha: string; lineas: readonly LineaDeApertura[]; modo?: ModoDeApertura },
): Promise<string> {
  const r = await pedirSeguro(pedir, '/api/apertura', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(datos),
  });
  const cuerpo = await leer<{ numero: string }>(r);
  return cuerpo.numero;
}

/* ═════════════════════════════════════════════════════════════════════════
   El cierre del mes
   ═════════════════════════════════════════════════════════════════════════

   Existía en la base desde E3 y no lo llamaba nadie: ni endpoint, ni pantalla,
   ni comando. Se podía cerrar un mes solo desde una suite de pruebas. */

export interface PasoDelCierre {
  readonly paso: number;
  readonly bloquea: boolean;
  /** Lo comprueba el sistema. Un paso comprobable NO se puede firmar. */
  readonly comprobable: boolean;
  readonly estado: 'cumplido' | 'afirmado' | 'pendiente' | 'no_aplica';
  /** Por qué está en ese estado, como CLAVE. La frase la arma la pantalla. */
  readonly motivo: string | null;
  /** El valor que la frase necesita — hoy, las fechas sin tipo de cambio. */
  readonly dato: string | null;
  readonly afirmado_por: string | null;
}

export interface LineaDeRevaluacion {
  readonly categoria: string;
  /** Texto, como todo el dinero de esta casa. */
  readonly diferencia: string;
  readonly asiento: string | null;
}

export async function traerPasosDelCierre(
  pedir: Pedir, anio: number, mes: number,
): Promise<PasoDelCierre[]> {
  const q = new URLSearchParams({ anio: String(anio), mes: String(mes) });
  const r = await pedirSeguro(pedir, `/api/cierre?${q.toString()}`);
  const cuerpo = await leer<{ pasos: PasoDelCierre[] }>(r);
  return cuerpo.pasos;
}

async function accionDeCierre<T>(
  pedir: Pedir, cuerpo: Record<string, unknown>,
): Promise<T> {
  const r = await pedirSeguro(pedir, '/api/cierre', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(cuerpo),
  });
  return leer<T>(r);
}

export async function revaluarPeriodo(
  pedir: Pedir, anio: number, mes: number,
): Promise<LineaDeRevaluacion[]> {
  const c = await accionDeCierre<{ revaluacion: LineaDeRevaluacion[] }>(
    pedir, { accion: 'revaluar', anio, mes });
  return c.revaluacion;
}

export async function afirmarPaso(
  pedir: Pedir, anio: number, mes: number, paso: number, nota: string | null,
): Promise<void> {
  await accionDeCierre(pedir, { accion: 'afirmar', anio, mes, paso, nota });
}

export async function cerrarPeriodo(
  pedir: Pedir, anio: number, mes: number,
): Promise<void> {
  await accionDeCierre(pedir, { accion: 'cerrar', anio, mes });
}

export async function reabrirPeriodo(
  pedir: Pedir, anio: number, mes: number,
): Promise<void> {
  await accionDeCierre(pedir, { accion: 'reabrir', anio, mes });
}

/**
 * Un ejercicio y en qué estado están sus doce meses.
 *
 * `futuros` es el estado que agrega la 0078: el periodo existe —para que el 1
 * de enero no pare el producto— y no se puede escribir en él hasta que alguien
 * abra el año.
 */
export interface Ejercicio {
  readonly anio: number;
  readonly futuros: number;
  readonly abiertos: number;
  readonly revaluados: number;
  readonly cerrados: number;
  readonly asientos: number;
  /**
   * Si este año se puede cerrar HOY.
   *
   * ▶▶ LO DICE LA BASE, y la pantalla no lo calcula. `cerrar_ejercicio` exige
   * los once primeros meses quietos y diciembre ABIERTO, y esa condición no se
   * puede deducir de los conteos: un año con enero abierto y diciembre cerrado
   * da `abiertos = 1` y `cerrados = 11` igual que uno listo para cerrar.
   *
   * Calcularlo acá sería una segunda definición de la misma regla — la del
   * botón que lo ofrece y la de la función que lo hace — y el día que una
   * cambiara, el botón se ofrecería sobre un año que la base rechaza. Es el
   * defecto que la `0090` arregló con «Abrir 2027».
   */
  readonly cerrable: boolean;
  /** Si ya tiene su asiento de cierre anual. Un segundo duplicaría el
   *  resultado del año en patrimonio, y la base lo rechaza con `SB211`. */
  readonly tiene_cierre: boolean;
}

export async function traerEjercicios(pedir: Pedir): Promise<Ejercicio[]> {
  const r = await pedirSeguro(pedir, '/api/cierre?ejercicios=1');
  const cuerpo = await leer<{ ejercicios: Ejercicio[] }>(r);
  return cuerpo.ejercicios;
}

/** Devuelve cuántos periodos pasaron de `futuro` a `abierto`. */
export async function abrirEjercicio(pedir: Pedir, anio: number): Promise<number> {
  const r = await accionDeCierre<{ abiertos?: number }>(
    pedir, { accion: 'abrir_ejercicio', anio });
  return r.abiertos ?? 0;
}

/**
 * Cerrar un ejercicio: el asiento que lleva el resultado del año a patrimonio.
 *
 * ▶ Devuelve el número del asiento, no un `ok`. El cierre anual no es un
 * trámite que se confirma: es un asiento de verdad, con sus líneas, y quien lo
 * corre tiene derecho a ir a verlo.
 */
export async function cerrarEjercicio(pedir: Pedir, anio: number): Promise<number> {
  const r = await accionDeCierre<{ asiento?: number }>(
    pedir, { accion: 'cerrar_ejercicio', anio });
  return r.asiento ?? 0;
}

/* ═════════════════════════════════════════════════════════════════════════
   El tablero
   ═════════════════════════════════════════════════════════════════════════

   Todo lo de esta pantalla salía de `datos/muestra.ts` desde la Fase 0. Los
   números vienen ahora de los mismos estados que dibujan los reportes: si el
   tablero los volviera a calcular, las dos pantallas podrían decir cifras
   distintas de la misma empresa y nadie sabría cuál vale. */

export interface ResumenDelTablero {
  readonly asientos: number;
  readonly activo: string;
  readonly pasivo: string;
  readonly patrimonio: string;
  readonly cuadra: boolean;
  readonly ingresos: string;
  readonly gastos: string;
  readonly utilidad: string;
  /** Hay mes anterior DENTRO del ejercicio. En enero, no. */
  readonly hay_previo: boolean;
  readonly ingresos_previo: string | null;
  readonly gastos_previo: string | null;
  readonly utilidad_previo: string | null;
  /**
   * La empresa tiene alguna cuenta declarada como efectivo.
   *
   * ▶ NO es lo mismo que un saldo en cero, y por eso viaja aparte: cero con
   * cuentas declaradas es que la empresa se quedó sin caja; cero sin ellas es
   * que nadie sabe cuánta caja tiene. Enseñar «₡ 0» en los dos casos pondría el
   * hueco con cara de dato.
   */
  readonly hay_efectivo: boolean;
  /** El SALDO al corte, no lo que se movió en el mes. */
  readonly efectivo: string | null;
  readonly efectivo_previo: string | null;
}

export interface PuntoDeLaSerie {
  readonly mes: number;
  readonly ingresos: string;
  readonly gastos: string;
}

export interface CuentaExpuesta {
  readonly cuenta: string;
  readonly nombre: string;
  readonly nombre_en: string;
  readonly moneda: string;
  /** El saldo en su moneda, y el mismo saldo como está en libros. */
  readonly saldo_original: string;
  readonly saldo_en_libros: string;
}

export async function traerTablero(
  pedir: Pedir, anio: number, mes: number,
): Promise<{
  resumen: ResumenDelTablero | null;
  serie: PuntoDeLaSerie[];
  expuestas: CuentaExpuesta[];
}> {
  const q = new URLSearchParams({ anio: String(anio), mes: String(mes) });
  const r = await pedirSeguro(pedir, `/api/tablero?${q.toString()}`);
  return leer(r);
}

/* ═════════════════════════════════════════════════════════════════════════
   La bitácora del inquilino
   ═════════════════════════════════════════════════════════════════════════

   ▶ ES UNA ENTRADA DEL PISO. El piso de suspensión promete leer y exportar los
   periodos cerrados, los estados y LA BITÁCORA. Nada de esto cuelga de la
   capacidad de escritura, ni acá ni en la API ni en la base: una empresa
   suspendida la lee, y es justo cuando más falta le hace — ahí está la orden
   que la suspendió. */

export interface EntradaDeBitacora {
  /** Un instante. No es una `date`, así que viaja como instante. */
  readonly ocurrio_en: string;
  /** Una CLAVE (`empresa.creada`, `periodo.cerrado`), no una frase. */
  readonly accion: string;
  /** Lo que la acción anotó. Su forma depende de la acción. */
  readonly detalle: Readonly<Record<string, unknown>>;
  readonly usuario_id: string | null;
  /** Quién lo hizo. `null` si el usuario ya no está: el hecho no se pierde. */
  readonly quien: string | null;
  readonly correo: string | null;
}

/**
 * La bitácora de la empresa, del hecho más reciente al más viejo.
 *
 * ▶ DEVUELVE EL SOBRE, como el diario y el mayor, y acá pesa más.
 *
 * Devolvía el array pelado y la base cortaba en 200 desde la 0039, así que la
 * pantalla mostraba doscientas filas sin manera de enterarse de que faltaban.
 * A las otras dos listas se entra a mirar; **a la bitácora se entra a buscar
 * un hecho concreto, casi siempre porque algo salió mal** — y quien no lo
 * encuentra en una lista que se presenta completa no concluye «está cortada»:
 * concluye que no ocurrió.
 */
export async function traerBitacora(
  pedir: Pedir, opciones: { desde?: string; hasta?: string; limite?: number } = {},
): Promise<Recorte<EntradaDeBitacora>> {
  const q = new URLSearchParams();
  if (opciones.desde) q.set('desde', opciones.desde);
  if (opciones.hasta) q.set('hasta', opciones.hasta);
  if (opciones.limite) q.set('limite', String(opciones.limite));
  const cola = q.toString();
  const r = await pedirSeguro(pedir, `/api/bitacora${cola ? `?${cola}` : ''}`);
  const cuerpo = await leer<{
    entradas: EntradaDeBitacora[]; hayMas?: boolean; limite?: number;
  }>(r);
  return {
    filas: cuerpo.entradas,
    hayMas: cuerpo.hayMas === true,
    limite: cuerpo.limite ?? cuerpo.entradas.length,
  };
}

/* ═════════════════════════════════════════════════════════════════════════
   La facturación electrónica
   ═════════════════════════════════════════════════════════════════════════

   ▶ EL EMISOR Y LOS COMPROBANTES VIAJAN JUNTOS porque los dibuja la misma
   pantalla, y en dos llamadas traerían una foto de cada momento.

   Y `emisor` puede ser `null`: **no hay emisor** es un hecho distinto de
   «hay uno a medio trámite», y de él depende que la pantalla diga «no se
   puede facturar todavía» o «falta un paso». */

/**
 * Quién le asigna el consecutivo a un emisor.
 *
 * ▶ Era `TramiteDelEmisor`, con cuatro pasos —`sin_iniciar`, `en_el_portal`,
 * `listo`, `apagado`— que caminaban el alta en el portal de un proveedor. Ese
 * proveedor se descartó el 10 de setiembre y la facturación se fue al hub, así
 * que los pasos hablaban de un sitio que no existe.
 *
 * Lo que el trámite decidía de verdad, debajo de los pasos, era **si Books
 * puede tomar un número de la secuencia legal**. Hoy no puede: la factura se
 * emite a mano y **ATV asigna el consecutivo**. Dos contadores sobre la misma
 * numeración dan saltos o repetidos, y un consecutivo no se mueve para atrás.
 */
export type QuienNumera = 'atv' | 'books' | 'apagado';

export interface EmisorDeLaEmpresa {
  readonly nombre: string;
  readonly tipo_identificacion: string;
  readonly identificacion: string;
  readonly sucursal: number;
  readonly terminal: number;
  readonly numeracion: QuienNumera;
  readonly activo: boolean;
}

/**
 * Los cinco estados acordados con el hub. Dicen el HECHO y no el mecanismo:
 * `facturaencr` tiene siete y cinco de ellos son su implementación.
 */
export type EstadoDeComprobante =
  | 'por_emitir' | 'esperando_a_hacienda' | 'aceptado' | 'rechazado' | 'fallido';

export interface ComprobanteReal {
  readonly id: string;
  readonly tipo: string;
  readonly consecutivo: string;
  readonly clave: string;
  readonly estado: EstadoDeComprobante;
  readonly intentos: number;
  readonly proveedor: string | null;
  readonly motivo: string | null;
  /** Texto, como todo el dinero de esta casa. Sale del documento que viajó. */
  readonly total: string | null;
  readonly moneda: string | null;
  /**
   * El asiento que registra el hecho económico, cuando lo hay.
   *
   * ▶ Puede ser `null` y no es un hueco: **un asiento se registra cuando el
   * hecho ocurre, no cuando Hacienda contesta**. Un comprobante puede nacer
   * antes que su asiento, y la mayoría de los asientos de una empresa no son
   * facturas. Lo que la 0060 sí impide es que apunte a uno que no existe.
   */
  readonly asiento: number | null;
  /**
   * La fecha CONTABLE del asiento, y NO es ninguna de las dos de abajo.
   *
   * ▶ `abierto_en` y `resuelto_en` son cuándo se movió el COMPROBANTE — cuándo
   * se abrió el trámite, cuándo Hacienda contestó. **Un asiento de marzo
   * tecleado en junio es legítimo**, así que enlazar al diario con `abierto_en`
   * abriría junio para un asiento de marzo: sin fallar y sin avisar.
   *
   * La trae la `0089` por unión con los asientos del inquilino, y no copiada a
   * `comprobantes`: un segundo sitio donde vive la misma fecha es un sitio
   * donde puede dejar de coincidir sin que nada lo denuncie.
   *
   * `null` cuando no hay asiento — el mismo `null` que `asiento`, y por el
   * mismo motivo.
   */
  readonly asiento_fecha: string | null;
  readonly abierto_en: string;
  readonly resuelto_en: string | null;
}

export interface Facturacion {
  /** `null` cuando la empresa no tiene emisor: no es lo mismo que no haber emitido. */
  readonly emisor: EmisorDeLaEmpresa | null;
  readonly comprobantes: Recorte<ComprobanteReal>;
}

export async function traerFacturacion(
  pedir: Pedir, limite?: number,
): Promise<Facturacion> {
  const q = new URLSearchParams();
  if (limite) q.set('limite', String(limite));
  const cola = q.toString();
  const r = await pedirSeguro(pedir, `/api/facturas${cola ? `?${cola}` : ''}`);
  const cuerpo = await leer<{
    emisor: EmisorDeLaEmpresa | null;
    comprobantes: ComprobanteReal[];
    hayMas?: boolean;
    limite?: number;
  }>(r);
  return {
    emisor: cuerpo.emisor,
    comprobantes: {
      filas: cuerpo.comprobantes,
      hayMas: cuerpo.hayMas === true,
      limite: cuerpo.limite ?? cuerpo.comprobantes.length,
    },
  };
}

/* ═════════════════════════════════════════════════════════════════════════
   El trámite de facturación, visto por el OPERADOR
   ═════════════════════════════════════════════════════════════════════════

   ▶ Existe para decir QUIÉN le asigna el consecutivo a cada cliente. Hoy la
   respuesta es «Hacienda, en ATV» para todos, y que se vea es lo que impide
   que Books tome números sobre una secuencia que ya lleva otro contador. */

export interface EmisorDeCliente {
  readonly codigo: string;
  readonly razon_social: string;
  /**
   * ▶ `false` NO es «numera ATV».
   *
   * Sin fila es que nadie ha decidido todavía que este cliente facture desde
   * acá; con fila y `atv` es que se decidió y el número lo pone Hacienda. Es la
   * diferencia entre «no vendido» y «vendido», y sólo la segunda tiene contador
   * abierto.
   */
  readonly tiene_emisor: boolean;
  readonly numeracion: QuienNumera | null;
  readonly identificacion: string | null;
  readonly provisional: boolean;
}

export async function darDeAltaEmisor(pedir: Pedir, codigo: string): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/operador', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ accion: 'alta_de_emisor', codigo }),
  });
  await leer(r);
}

/**
 * Declara quién le asigna el consecutivo a un emisor.
 *
 * ▶ Va explícito y no se deduce del estado actual, y acá pesa más que antes:
 * decir `books` abre la puerta a que este despliegue tome números de una
 * secuencia legal que hoy lleva Hacienda. Queda en la bitácora con nombre.
 */
export async function declararNumeracion(
  pedir: Pedir, codigo: string, quien: QuienNumera,
): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/operador', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ accion: 'declarar_numeracion', codigo, quien }),
  });
  await leer(r);
}

/**
 * Declarar que la empresa nace acá y no tiene saldos anteriores.
 *
 * ▶ No es «cargar una apertura vacía». Una apertura vacía sería un asiento sin
 * líneas —que ni cuadra ni significa nada—; esto es una afirmación con fecha y
 * con autor, que la bitácora guarda. La diferencia importa el día que alguien
 * pregunte por qué esta empresa no tiene saldos iniciales.
 */
export async function declararSinSaldosPrevios(pedir: Pedir, desde: string): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/apertura', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ desde }),
  });
  await leer(r);
}

/* ═══════════════════════════════════════════════════════════════════════════
   Activos y depreciación
   ═════════════════════════════════════════════════════════════════════════ */

/** Un bien que se deprecia. Las tres cuentas y las fechas vienen como TEXTO. */
export interface ActivoDeLaEmpresa {
  readonly id: number;
  readonly descripcion: string;
  readonly cuenta_activo: string;
  readonly cuenta_depreciacion: string;
  readonly cuenta_gasto: string;
  readonly fecha_compra: string;
  /** Desde cuándo se deprecia. Va aparte de la compra: la norma cuenta desde
   *  que el bien está disponible para usarse, y no siempre es el día de la factura. */
  readonly inicio: string;
  readonly costo: string;
  readonly valor_residual: string;
  readonly vida_meses: number;
  /**
   * La vida que Hacienda admite, cuando NO es la contable.
   *
   * ▶ `null` significa que no difiere —el caso de casi todos—, no «sin
   * declarar». Cuando difiere, el gasto que se deduce en la declaración no es
   * el de los libros, y la diferencia sale en el D-100 (`0077`).
   */
  readonly vida_fiscal_meses: number | null;
  readonly clasificacion: Record<string, string>;
  readonly dado_de_baja: string | null;
  readonly moneda: string;
}

/** Lo que a un bien le tocaría este mes, sin escribir nada. */
export interface CuotaPrevista {
  readonly activo: number;
  readonly descripcion: string;
  readonly cuenta_gasto: string;
  readonly cuenta_depreciacion: string;
  readonly cuota: string;
  readonly mes_de_vida: number;
  readonly vida_meses: number;
  /** El último mes lleva LO QUE FALTA y no la cuota, para que la acumulada
   *  cierre exacta contra el costo menos el residual. */
  readonly ultimo: boolean;
  readonly clasificacion: Record<string, string>;
  readonly moneda: string;
}

export async function traerActivos(
  pedir: Pedir, anio: number, mes: number,
): Promise<{ activos: ActivoDeLaEmpresa[]; previo: CuotaPrevista[] }> {
  const q = new URLSearchParams({ anio: String(anio), mes: String(mes) });
  const r = await pedirSeguro(pedir, `/api/activos?${q.toString()}`);
  return leer<{ activos: ActivoDeLaEmpresa[]; previo: CuotaPrevista[] }>(r);
}

export async function agregarActivo(pedir: Pedir, a: {
  descripcion: string; cuentaActivo: string; cuentaDepreciacion: string;
  cuentaGasto: string; fechaCompra: string; inicio: string; costo: string;
  vidaMeses: number; valorResidual: string; clasificacion: Record<string, string>;
  /** La vida fiscal, si difiere. Omitirla es decir que no difiere. */
  vidaFiscalMeses?: number | null;
}): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/activos', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(a),
  });
  await leer(r);
}

/**
 * Depreciar el mes.
 *
 * ▶ Es PUT y no POST porque correrlo dos veces deja el MISMO resultado: la
 * función borra el asiento anterior y lo regenera. Un POST insinuaría que la
 * segunda vez agrega otro.
 */
export async function depreciarPeriodo(
  pedir: Pedir, anio: number, mes: number,
): Promise<{ activos: number; total: string; asiento: number | null }> {
  const q = new URLSearchParams({ anio: String(anio), mes: String(mes) });
  const r = await pedirSeguro(pedir, `/api/activos?${q.toString()}`, { method: 'PUT' });
  return leer<{ activos: number; total: string; asiento: number | null }>(r);
}

/* ═══════════════════════════════════════════════════════════════════════════
   EL DEVENGO DE INGRESOS DIFERIDOS (0102)
   ═══════════════════════════════════════════════════════════════════════════

   Lo cobrado por adelantado no es ingreso del mes: es una obligación que se
   gana a lo largo del plazo — NIIF 15. Los importes viajan como TEXTO, igual
   que todo el dinero de esta casa. */

/** Una obligación del auxiliar, con lo que lleva ganado y lo que le falta. */
export interface ObligacionDiferida {
  readonly id: number;
  readonly descripcion: string;
  readonly contraparte: string;
  readonly contraparte_nombre: string;
  readonly cuenta_diferida: string;
  readonly cuenta_ingreso: string;
  /** La factura que la originó. `null` cuando viene de la apertura. */
  readonly documento: number | null;
  readonly fecha: string;
  /** Desde cuándo se devenga. Va APARTE de la fecha: se factura por anticipado
   *  y el servicio arranca después. */
  readonly inicio: string;
  readonly meses: number;
  readonly importe: string;
  readonly moneda: string;
  /** Lo que se va a reconocer, fijado al tipo de cambio del documento: un
   *  pasivo de contrato no es monetario y no se revalúa. */
  readonly importe_funcional: string;
  /** ▶ Salen del CALENDARIO, no de los libros: son lo que DEBERÍA estar
   *  devengado. Compararlos contra la contabilidad es lo que hace el cuadre, y
   *  si salieran del libro coincidirían siempre. */
  readonly devengado: string;
  readonly pendiente: string;
  readonly clasificacion: Record<string, string>;
  readonly dado_de_baja: string | null;
  readonly moneda_local: string;
}

/** Lo que se devengaría en el mes, sin escribir nada. */
export interface CuotaDeDevengo {
  readonly obligacion: number;
  readonly descripcion: string;
  readonly contraparte: string;
  readonly cuenta_diferida: string;
  readonly cuenta_ingreso: string;
  readonly cuota: string;
  readonly mes_de_plazo: number;
  readonly meses: number;
  /** El último mes lleva LO QUE FALTA y no la cuota, para que la obligación
   *  cierre exacta en cero. */
  readonly ultimo: boolean;
  readonly clasificacion: Record<string, string>;
  readonly moneda: string;
}

/** El auxiliar contra la contabilidad. Una discrepancia NO rompe ningún total. */
export interface CuadreDeDiferidos {
  readonly pendiente_auxiliar: string;
  readonly saldo_contable: string;
  readonly diferencia: string;
  readonly cuadra: boolean;
  readonly obligaciones: number;
  /** ▶▶ EL SEGUNDO AUXILIAR (`0114`). El ingreso diferido de una empresa tiene dos
   *  fuentes —las obligaciones por calendario y los proyectos por avance— y este
   *  cuadre reconciliaba sólo la primera: cada proyecto con factura emitida lo
   *  dejaba roto por su pendiente, un descuadre que no se arreglaba registrando
   *  nada. Las dos cifras viajan SEPARADAS porque un total sumado diría que algo
   *  se movió sin decir cuál de los dos detalles hay que abrir. */
  readonly pendiente_proyectos: string;
  readonly proyectos: number;
  /** Los que este cuadre NO cuenta: un proyecto cuya cuenta de contrato es de
   *  ACTIVO no tiene su saldo en el diferido. Sale a la vista porque excluirlo en
   *  silencio es exactamente cómo un cuadre cuadra por casualidad. */
  readonly proyectos_fuera: number;
  /** Lo que vence a más de doce meses del corte. Se ENSEÑA para reclasificarlo
   *  a no corriente a mano: asentarlo pide un segundo asiento mensual de ida y
   *  vuelta, y eso se construye contra un contrato largo de verdad. */
  readonly no_corriente: string;
}

export async function traerDiferidos(
  pedir: Pedir, anio: number, mes: number,
): Promise<{
  obligaciones: ObligacionDiferida[];
  previo: CuotaDeDevengo[];
  cuadre: CuadreDeDiferidos | null;
}> {
  const q = new URLSearchParams({ anio: String(anio), mes: String(mes) });
  const r = await pedirSeguro(pedir, `/api/diferidos?${q.toString()}`);
  return leer(r);
}

export async function agregarObligacion(pedir: Pedir, o: {
  descripcion: string; contraparte: string; cuentaDiferida: string;
  cuentaIngreso: string; fecha: string; inicio: string; importe: string;
  meses: number; moneda?: string | null; clasificacion: Record<string, string>;
}): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/diferidos', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(o),
  });
  await leer(r);
}

/**
 * Devengar el mes.
 *
 * ▶ Es PUT y no POST porque correrlo dos veces deja el MISMO resultado: la
 * función borra el asiento anterior y lo regenera. Un POST insinuaría que la
 * segunda vez reconoce el ingreso otra vez — que es justo el error silencioso
 * que esto evita.
 */
export async function devengarPeriodo(
  pedir: Pedir, anio: number, mes: number,
): Promise<{ obligaciones: number; total: string; asiento: number | null }> {
  const q = new URLSearchParams({ anio: String(anio), mes: String(mes) });
  const r = await pedirSeguro(pedir, `/api/diferidos?${q.toString()}`, { method: 'PUT' });
  return leer(r);
}

/**
 * Dar de baja una obligación — la modificación, prospectiva.
 *
 * ▶ Es PATCH y no DELETE: no borra nada, le pone fecha de fin a algo que sigue
 * existiendo con su historia devengada intacta. Y NO reconoce el pendiente como
 * ingreso: que la empresa tenga derecho a quedarse con lo cobrado es un juicio
 * del contrato, no un hecho del calendario.
 */
export async function darDeBajaObligacion(
  pedir: Pedir, id: number, fecha: string,
): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/diferidos', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ id, fecha }),
  });
  await leer(r);
}

/* ═══════════════════════════════════════════════════════════════════════════
   LA PROVISIÓN DE RENTA Y EL DETERIORO DE CARTERA (0103)
   ═══════════════════════════════════════════════════════════════════════════

   ▶▶ LOS DOS SON UN SALDO OBJETIVO, no un gasto del mes. Lo que se asienta es
   la DIFERENCIA contra lo que ya hay: tratarlos como gasto mensual los acumula
   doce veces y el balance cuadra todo el tiempo. */

/** Un renglón del deterioro previsto. Los dos últimos no son tramos. */
export interface TramoDeDeterioro {
  /** Un tramo de antigüedad, o `ESTIMACION` —lo que hoy hay en la cuenta— o
   *  `AJUSTE` —lo que falta para llegar al objetivo, que es lo que se asienta—. */
  readonly tramo: string;
  /** Lo que la cartera debe en ese tramo. `null` en las dos filas de cierre. */
  readonly cartera: string | null;
  readonly porcentaje: string | null;
  readonly requerido: string;
  readonly confirmada: boolean;
  readonly moneda: string;
}

/** Un tramo de la matriz declarada. */
export interface TramoDeLaMatriz {
  readonly tramo: string;
  readonly porcentaje: string;
  /** Falso mientras sea la propuesta de la casa y no la estimación de la
   *  empresa. El paso 8 del cierre no da por cumplido lo que nadie miró. */
  readonly confirmada: boolean;
}

/** La provisión de renta prevista. `null` cuando no se pudo calcular. */
export interface ProvisionDeRenta {
  /** La utilidad antes de impuesto — la casilla 50, no una suma nueva. */
  readonly base: string;
  readonly tasa: string;
  readonly requerido: string;
  readonly ya_provisionado: string;
  readonly ajuste: string;
  readonly moneda: string;
}

export async function traerProvisiones(
  pedir: Pedir, anio: number, mes: number,
): Promise<{
  deterioro: TramoDeDeterioro[];
  matriz: TramoDeLaMatriz[];
  renta: ProvisionDeRenta | null;
  /** El código por el que la renta no se pudo calcular — `SB237` si falta la
   *  tasa. Viaja aparte para que la pantalla diga qué falta y dónde. */
  rentaCodigo: string | null;
  /** Las diferencias temporarias del año y su efecto — NIC 12, `0113`. */
  diferido: DiferenciaTemporaria[];
}> {
  const q = new URLSearchParams({ anio: String(anio), mes: String(mes) });
  const r = await pedirSeguro(pedir, `/api/provisiones?${q.toString()}`);
  return leer(r);
}

/**
 * Provisionar el mes.
 *
 * ▶ Es PUT porque correrlo dos veces deja el MISMO resultado: la función borra
 * el asiento anterior y lo rehace contra el objetivo de hoy. Un POST insinuaría
 * que la segunda vez suma otra provisión encima — que es el error que esto
 * existe para impedir.
 */
export async function provisionar(
  pedir: Pedir, que: 'renta' | 'deterioro' | 'diferido', anio: number, mes: number,
): Promise<{ ajuste: string; requerido?: string; objetivo?: string;
             asiento: number | null }> {
  const q = new URLSearchParams({ anio: String(anio), mes: String(mes), que });
  const r = await pedirSeguro(pedir, `/api/provisiones?${q.toString()}`, { method: 'PUT' });
  return leer(r);
}

/** Declarar la matriz de deterioro — y con eso deja de ser la propuesta nuestra. */
export async function declararMatriz(
  pedir: Pedir, tramos: Record<string, string>,
): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/provisiones', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    // ▶ La acción va explícita desde la `0113`: la puerta atiende tres cosas
    // por POST y deducir cuál es por los campos que vinieron hace que una
    // petición mal formada se interprete como otra en vez de rebotar.
    body: JSON.stringify({ accion: 'matriz', tramos }),
  });
  await leer(r);
}

/* ═══════════════════════════════════════════════════════════════════════════
   EL AVANCE DE LOS PROYECTOS (0104)
   ═══════════════════════════════════════════════════════════════════════════

   ▶▶ EL AVANCE SE PIDE, NO SE MIDE. El modelo pone la planilla propia en OPEX
   y sin `project`, así que un método de costos vería sólo los subcontratistas y
   dejaría todos los proyectos al diez por ciento con cara de medida. Lo declara
   una persona, con fecha y con nombre; de ahí en adelante es aritmética. */

/** Un proyecto con lo que habría que reconocerle al corte. */
export interface AvancePrevisto {
  readonly proyecto: string;
  readonly nombre: string;
  readonly contraparte: string;
  /** ▶ Se llamaba `cuenta_activo` y el nombre empujaba al flujo equivocado: la
   *  pantalla ofrecía `1115` y el saldo de la factura se quedaba en `2133` sin que
   *  ningún auxiliar lo reclamara. Con la factura primero —el proceso real— es la
   *  cuenta de ingreso DIFERIDO. `0114`. */
  readonly cuenta_diferida: string;
  readonly cuenta_ingreso: string;
  readonly precio: string;
  /** El último avance declarado HASTA el corte. `null` si nunca se declaró. */
  readonly porcentaje: string | null;
  /** De qué mes es esa declaración — no siempre es el mes que se mira, y eso
   *  importa: no declarar significa «sigue valiendo lo de la última vez». */
  readonly declarado_anio: number | null;
  readonly declarado_mes: number | null;
  /** Precio × avance: el SALDO OBJETIVO de ingreso reconocido. */
  readonly objetivo: string;
  readonly ya_reconocido: string;
  /** Lo que se asentaría. Negativo cuando el avance se corrigió hacia abajo. */
  readonly ajuste: string;
  readonly cerrado: boolean;
  /** La categoría del Módulo 5, y si cuenta como backlog viable. `null` en las
   *  dos es «sin categorizar» — que no es lo mismo que no viable. */
  readonly categoria: string | null;
  readonly viable: boolean | null;
  /** Las horas cargadas del proyecto hasta el corte. */
  readonly horas_ejecutadas: string;
  /** `null` es «no hay presupuesto», y entonces no se sugiere nada. */
  readonly horas_presupuestadas: string | null;
  /**
   * Lo que las horas SUGIEREN, y nada más que sugieren.
   *
   * ▶▶ El avance se declara porque es un juicio —lo decidió la `0104` con el
   * modelo en la mano— y esto sólo pone el número delante de quien decide.
   * `null` significa que no hay con qué sugerir, y la pantalla lo dice.
   */
  readonly sugerido: string | null;
  /**
   * Lo reconocido entre las horas facturables consumidas — POR HORA.
   *
   * ▶▶ El caso Wally del Módulo 5: *sobrepasar los días de contrato regala
   * margen.* El precio no cambia y las horas sí, así que cada hora de más baja lo
   * que se cobra por hora **sin que ningún número del libro se mueva**.
   */
  readonly tarifa_efectiva: string | null;
  /** El precio entre las horas presupuestadas. `null` sin presupuesto. */
  readonly tarifa_contratada: string | null;
  /**
   * La efectiva es menor que la contratada.
   *
   * ▶ Sólo cuando las DOS existen: con una en null la comparación no existe, y
   * una alerta sin comparación es una alarma que nadie puede apagar.
   */
  readonly alerta_tarifa: boolean;
  readonly moneda: string;
}

/** Una línea del backlog: lo firmado y no reconocido de un proyecto abierto. */
export interface LineaDeBacklog {
  readonly proyecto: string;
  readonly nombre: string;
  readonly contraparte: string;
  readonly precio: string;
  readonly reconocido: string;
  readonly backlog: string;
  readonly categoria: string | null;
  readonly categoria_nombre: string | null;
  readonly categoria_nombre_en: string | null;
  readonly color: string | null;
  readonly viable: boolean | null;
  readonly moneda: string;
}

/** El backlog total y qué parte es viable. */
export interface ResumenDeBacklog {
  readonly total: string;
  readonly viable: string;
  readonly no_viable: string;
  readonly sin_categoria: string;
  /** `null` cuando no hay backlog: un cero por ciento sobre una cartera vacía
   *  se lee como una alarma, y lo que pasa es que no hay nada que planificar. */
  readonly viable_pct: string | null;
  readonly proyectos: number;
  readonly sin_categorizar: number;
  readonly moneda: string;
}

/** Una de las cinco del Módulo 5, tal como las declara el modelo. */
export interface CategoriaDeBacklog {
  readonly clave: string;
  readonly nombre: string;
  readonly nombre_en: string;
  readonly color: string;
  readonly viable: boolean;
  readonly orden: number;
}

/** El auxiliar de proyectos contra los libros. */
export interface CuadreDeProyectos {
  readonly objetivo: string;
  readonly reconocido: string;
  readonly diferencia: string;
  readonly cuadra: boolean;
  /**
   * El saldo de las cuentas de contrato que los proyectos declaran.
   *
   * ▶▶ Se llamaba `activo_contable` y desde la `0114` era mentira: con la factura
   * primero, ese saldo es un PASIVO —ingreso diferido— y no un activo. Y sale de
   * las cuentas que los proyectos DECLARAN, no de un `1115` escrito en la función:
   * con los proyectos apuntando al diferido, aquel literal habría seguido dando
   * cero sobre una cuenta vacía.
   */
  readonly saldo_contrato: string;
  readonly proyectos: number;
  /** Proyectos cuyo activo de contrato quedó ACREEDOR: eso es un pasivo de
   *  contrato con el disfraz de un activo, y su sitio es la `2133`. */
  readonly en_negativo: number;
  readonly moneda: string;
}

/** La moneda del contrato de un proyecto y su precio, en esa moneda (0141). */
export interface MonedaDeProyecto {
  readonly codigo: string;
  readonly moneda: string;
  readonly precio: string;
}

/**
 * La rentabilidad de GESTIÓN de un proyecto (0144). No sale de asientos: el
 * costo es las horas por el costo por hora de cada persona, y se mira, no se
 * escribe. El margen puede ser negativo.
 */
export interface RentabilidadDeProyecto {
  readonly proyecto: string;
  readonly nombre: string;
  readonly ingreso: string;
  readonly costo: string;
  readonly horas: string;
  readonly margen: string;
  /** `null` cuando el ingreso es cero: un porcentaje sobre cero no dice nada. */
  readonly margen_pct: string | null;
  /** Backlog aún no reportó el costo de este proyecto hasta este mes: no se
   *  puede costear todavía. Distinto de un margen de cero. */
  readonly sin_costo: boolean;
  /** El costo llegó en una moneda distinta a la del proyecto: no se resta (§6.1).
   *  El margen no es de fiar. */
  readonly costo_otra_moneda: boolean;
  readonly moneda: string;
}

/** La utilización de una persona en el mes, del desglose que reporta Backlog
 *  (0151). Los minutos van enteros; la pantalla los pasa a horas. */
export interface UtilizacionDePersona {
  readonly persona: string;
  readonly minutos: number;
  readonly minutos_facturable: number;
}

export async function traerProyectos(
  pedir: Pedir, anio: number, mes: number,
): Promise<{
  previo: AvancePrevisto[];
  cuadre: CuadreDeProyectos | null;
  backlog: LineaDeBacklog[];
  resumen: ResumenDeBacklog | null;
  categorias: CategoriaDeBacklog[];
  // La moneda del CONTRATO de cada proyecto (0141). El `precio` está en ESA
  // moneda; `avance_previsto.moneda` es la de los libros, para los colones.
  monedas: MonedaDeProyecto[];
  rentabilidad: RentabilidadDeProyecto[];
  utilizacion: UtilizacionDePersona[];
}> {
  const q = new URLSearchParams({ anio: String(anio), mes: String(mes) });
  const r = await pedirSeguro(pedir, `/api/proyectos?${q.toString()}`);
  return leer(r);
}

export async function agregarProyecto(pedir: Pedir, p: {
  codigo: string; nombre: string; contraparte: string;
  cuentaDiferida: string; cuentaIngreso: string; precio: string; inicio: string;
  // ▶ El `numero` de Backlog (HUB-148): opcional, entero. Vacío = sin numero.
  numero?: string;
  // ▶ La moneda del contrato (0141): vacío = la de los libros.
  moneda?: string;
}): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/proyectos', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(p),
  });
  await leer(r);
}

export async function cerrarProyecto(
  pedir: Pedir, codigo: string, fecha: string,
): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/proyectos', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ accion: 'cerrar', codigo, fecha }),
  });
  await leer(r);
}

/**
 * Reconocer el avance del mes.
 *
 * ▶ Es PUT porque correrlo dos veces deja el MISMO resultado: se asienta la
 * diferencia contra un objetivo que no cambia. Un POST insinuaría que la
 * segunda vez reconoce otra vez.
 */
export async function reconocerAvance(
  pedir: Pedir, anio: number, mes: number,
): Promise<{ proyectos: number; total: string; asiento: number | null }> {
  const q = new URLSearchParams({ anio: String(anio), mes: String(mes) });
  const r = await pedirSeguro(pedir, `/api/proyectos?${q.toString()}`, { method: 'PUT' });
  return leer(r);
}

/* ═══════════════════════════════════════════════════════════════════════════
   LA CONCILIACIÓN BANCARIA (0066)
   ═══════════════════════════════════════════════════════════════════════════

   ▶ Los nombres de este bloque dicen `entrada`/`salida` y no `debe`/`haber`, y
   eso viaja igual desde la base. No es una traducción pendiente: lo que el banco
   llama «crédito» es plata que ENTRÓ, y en nuestro libro eso es un DEBE.
   Traducirlo acá abriría un segundo sitio donde equivocarse — la traducción
   ocurre una sola vez, en `control.registrar_desde_banco`. */

/** Una cuenta que ya tiene extracto cargado. Para proponer, no para decidir. */
export interface CuentaConExtracto {
  readonly cuenta: string;
  readonly nombre: string;
  readonly filas: number;
  readonly pendientes: number;
}

/** Un movimiento del libro sobre la cuenta de banco. */
export interface LineaDeLibro {
  readonly asiento: number;
  readonly linea: number;
  readonly fecha: string;
  readonly glosa: string;
  readonly detalle: string | null;
  readonly debe: string;
  readonly haber: string;
  /** Pendiente A FIN DE MES, que no es lo mismo que pendiente hoy: un par
   *  emparejado se resuelve en la más tardía de sus dos fechas. */
  readonly pendiente: boolean;
  /** Con qué fila del extracto quedó emparejado, si lo está. */
  readonly banco: number | null;
  readonly moneda: string;
}

/** Una fila del estado bancario. */
export interface LineaDeBanco {
  readonly id: number;
  readonly fecha: string;
  readonly descripcion: string;
  readonly referencia: string | null;
  readonly entrada: string;
  readonly salida: string;
  readonly pendiente: boolean;
  readonly asiento: number | null;
  readonly linea: number | null;
  readonly moneda: string;
}

/**
 * Las dos columnas de la conciliación.
 *
 * ▶ `saldo_banco` en `null` es «no se declaró», y NO es cero. Los tres números
 * que dependen de él —`ajustado_banco`, `diferencia`— viajan también en `null`
 * para que la pantalla no pueda enseñar una diferencia inventada.
 */
export interface CuadreBancario {
  readonly saldo_libros: string;
  readonly saldo_banco: string | null;
  readonly depositos_en_transito: string;
  readonly cheques_girados: string;
  readonly notas_credito: string;
  readonly notas_debito: string;
  readonly ajustado_libros: string;
  readonly ajustado_banco: string | null;
  readonly diferencia: string | null;
  readonly pendientes_libro: number;
  readonly pendientes_banco: number;
  readonly filas_banco: number;
  /**
   * Cuántas filas del extracto están ya emparejadas.
   *
   * ▶▶ CON CERO, LOS DOS «SALDOS AJUSTADOS» NO SIGNIFICAN NADA: cada partida se
   * cuenta en los dos lados a la vez. Era el hallazgo 3 del §13.1 —*un número
   * con forma de resultado, antes de ser un resultado*— y la aritmética era
   * correcta: lo que faltaba era esto para no presentarla como conclusión.
   */
  readonly emparejados: number;
  readonly moneda: string;
}

/** Una cuenta que el catálogo declara como efectivo: caja o banco. */
export interface CuentaDeEfectivo {
  readonly codigo: string;
  readonly nombre: string;
  readonly nombre_en: string;
  readonly moneda: string | null;
}

/**
 * Todo lo de la pantalla en una respuesta.
 *
 * Las tres lecturas viajan juntas a propósito: el cuadre dice «faltan ₡2 500» y
 * la lista de pendientes tiene que ser la que produce esos ₡2 500. Tres llamadas
 * darían tres fotos de tres momentos.
 *
 * Con `cuenta` en `null` —nadie ha elegido todavía— sólo vienen las cuentas, y
 * eso no es un error: es la primera vez que alguien abre la pantalla.
 */
/** Una cuenta de efectivo con —o sin— su cuenta del banco declarada. */
export interface CuentaBancaria {
  readonly cuenta: string;
  readonly nombre: string;
  readonly nombre_en: string | null;
  /** La declara la CUENTA contable, no la fila del banco: tenerla dos veces es
   *  tenerla mal el día que alguien cambie una. */
  readonly moneda: string;
  readonly banco: string | null;
  readonly numero: string | null;
  readonly titular: string | null;
  readonly activa: boolean;
}

export interface Conciliacion {
  readonly cuentas: CuentaConExtracto[];
  /**
   * Las que se pueden ELEGIR, que no son las que ya tienen extracto.
   *
   * ▶ Vacía significa que el catálogo no declara ninguna cuenta de efectivo, y
   * la pantalla lo dice. No hay lista de reserva: una segunda lista «por si
   * acaso» es el sitio por donde vuelve a colarse depreciación acumulada.
   */
  readonly efectivo: CuentaDeEfectivo[];
  /** Las cuentas del banco DE VERDAD, atadas a su cuenta contable (`0118`).
   *  Trae también las de efectivo que **todavía no** tienen banco puesto: listar
   *  sólo las declaradas escondió justo el trabajo que falta. */
  readonly bancarias: CuentaBancaria[];
  readonly libro: LineaDeLibro[] | null;
  readonly extracto: LineaDeBanco[] | null;
  readonly cuadre: CuadreBancario | null;
}

/** El saldo de una cuenta de caja o banco a una fecha, en SU moneda. */
export interface SaldoDeEfectivo {
  readonly codigo: string;
  readonly nombre: string;
  readonly nombre_en: string;
  readonly moneda: string;
  /** En la moneda de la cuenta, como texto. */
  readonly saldo: string;
}

/** Los saldos de caja y bancos a una fecha de corte (0135). Un cierre es su día. */
export async function traerSaldosDeEfectivo(
  pedir: Pedir, fecha: string,
): Promise<{ fecha: string; saldos: readonly SaldoDeEfectivo[] }> {
  const r = await pedirSeguro(pedir, `/api/saldos?fecha=${encodeURIComponent(fecha)}`); // regla-ok: «saldos» es la ruta del endpoint, no un importe
  return leer<{ fecha: string; saldos: readonly SaldoDeEfectivo[] }>(r);
}

export async function traerConciliacion(
  pedir: Pedir, cuenta: string | null, anio: number, mes: number,
): Promise<Conciliacion> {
  const q = new URLSearchParams({ anio: String(anio), mes: String(mes) });
  if (cuenta) q.set('cuenta', cuenta);
  const r = await pedirSeguro(pedir, `/api/banco?${q.toString()}`);
  return leer<Conciliacion>(r);
}

/** Una fila tal como la manda la pantalla: importes ya canónicos, fecha ISO. */
export interface FilaParaImportar {
  readonly fecha: string;
  readonly descripcion: string;
  readonly referencia: string;
  readonly entrada: string;
  readonly salida: string;
}

async function accion<T>(pedir: Pedir, cuerpo: Record<string, unknown>): Promise<T> {
  const r = await pedirSeguro(pedir, '/api/banco', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(cuerpo),
  });
  return leer<T>(r);
}

/**
 * Traer las filas del extracto.
 *
 * Devuelve cuántas entraron y cuántas ya estaban, y **las dos se enseñan**:
 * «importé 40 filas» sobre un archivo de 120 es exactamente lo que alguien
 * necesita ver para entender que subió el archivo de dos meses otra vez.
 */
export async function importarExtracto(
  pedir: Pedir, cuenta: string, filas: readonly FilaParaImportar[],
): Promise<{ nuevas: number; repetidas: number }> {
  return accion(pedir, { accion: 'importar', cuenta, filas });
}

/** Empareja sólo lo que no admite duda. Lo ambiguo lo devuelve contado. */
export async function emparejarAutomatico(
  pedir: Pedir, cuenta: string, anio: number, mes: number, dias: number,
): Promise<{ emparejados: number; ambiguos: number }> {
  return accion(pedir, { accion: 'emparejar', cuenta, anio, mes, dias });
}

export async function conciliarFila(
  pedir: Pedir, id: number, asiento: number, linea: number,
): Promise<void> {
  await accion(pedir, { accion: 'conciliar', id, asiento, linea });
}

export async function desconciliarFila(pedir: Pedir, id: number): Promise<void> {
  await accion(pedir, { accion: 'desconciliar', id });
}

/**
 * Escribir el asiento que le falta a una nota del banco.
 *
 * La contrapartida la elige la persona y no se adivina por la descripción: la
 * misma palabra es un gasto en una empresa y una cuenta por cobrar en otra.
 */
export async function registrarDesdeBanco(
  pedir: Pedir, id: number, contrapartida: string, glosa: string,
  clasificacion: Record<string, string>,
): Promise<{ asiento: number | null }> {
  return accion(pedir, { accion: 'registrar', id, contrapartida, glosa, clasificacion });
}

/**
 * Declarar el saldo que dice el estado bancario.
 *
 * Es el único dato de la conciliación que no está en el sistema: lo dice el
 * papel. El importe viaja como TEXTO — §6.1.
 */
export async function declararSaldoBanco(
  pedir: Pedir, cuenta: string, anio: number, mes: number, saldo: string,
): Promise<void> {
  await accion(pedir, { accion: 'saldo', cuenta, anio, mes, saldo });
}

export async function declararCuentaBancaria(pedir: Pedir, d: {
  cuenta: string; banco: string; numero: string; titular?: string | null;
}): Promise<void> {
  await accion(pedir, { accion: 'declarar_cuenta', ...d });
}

/**
 * Mueve dinero entre dos cuentas de efectivo propias (`0118`).
 *
 * ▶▶ `sale` y `entra` son DOS importes y viajan como texto. Entre monedas
 * distintas no son el mismo número y no se pueden derivar uno del otro: el banco
 * convierte con SU tipo, no con el del maestro, y lo que sobra o falta contra el
 * tipo del día es un diferencial que la base reconoce. `entra` en `null` es «entra
 * lo mismo que sale», que es lo correcto con una sola moneda.
 */
export async function transferirEntreCuentas(pedir: Pedir, d: {
  origen: string; destino: string; fecha: string; sale: string;
  entra?: string | null; glosa?: string | null; detalle?: string | null;
}): Promise<string | null> {
  const r = await accion(pedir, { accion: 'transferir', ...d });
  return (r as { asiento?: string | null }).asiento ?? null;
}

/* ═══════════════════════════════════════════════════════════════════════════
   LA CONFIGURACIÓN DE LA EMPRESA (0069)
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Lo que la empresa puede cambiar de sí misma, y lo que no.
 *
 * ▶ `asientos` no es un dato de adorno: es la razón por la que la moneda se
 * puede o no se puede cambiar, y además la FRASE que la pantalla enseña. Un
 * booleano «se puede» obligaría a inventarse el porqué.
 */
export interface ConfiguracionDeLaEmpresa {
  readonly codigo: string;
  readonly razon_social: string;
  readonly moneda_local: string;
  readonly idioma_por_omision: 'es' | 'en';
  /**
   * La tarifa del impuesto sobre la renta, en por ciento, COMO TEXTO.
   *
   * ▶ `null` no es cero: cero por ciento es una tasa declarada —una zona
   * franca— y `null` es que nadie la declaró. El paso 7 del cierre dice cosas
   * distintas en cada caso, y un `number` en el borde borraría la diferencia.
   */
  readonly tasa_de_renta: string | null;
  readonly tipo_identificacion: string;
  readonly identificacion: string | null;
  readonly identificacion_cruda: string;
  /** Si esa identificación es un relleno nuestro y no la real. */
  readonly identificacion_provisional: boolean;
  /** Cuántos asientos hay escritos. Cierran el cambio de moneda. */
  readonly asientos: number;
  readonly estado: string;
  readonly es_demo: boolean;
}

/** El estado de la apertura, como lo devuelve la base. */
export interface AperturaEnConfiguracion {
  readonly cargada: boolean;
  readonly moneda_local: string;
  /** `null` NO es «sí hay saldos previos»: es que nadie lo ha declarado. */
  readonly sin_saldos_previos: string | null;
}

export interface PaqueteDeConfiguracion {
  /** Para saber qué OFRECER. Quién puede de verdad lo decide la base. */
  readonly rol: 'admin' | 'contador' | 'solo_lectura';
  readonly configuracion: ConfiguracionDeLaEmpresa | null;
  readonly apertura: AperturaEnConfiguracion | null;
}

export async function traerConfiguracion(pedir: Pedir): Promise<PaqueteDeConfiguracion> {
  const r = await pedirSeguro(pedir, '/api/configuracion');
  return leer<PaqueteDeConfiguracion>(r);
}

/**
 * Guardar la configuración.
 *
 * ▶ Es PUT y van los tres campos siempre: guardar dos veces lo mismo deja el
 * mismo resultado, y «no lo tocó» y «lo dejó igual» pasan a ser el mismo caso.
 * La base compara y anota en la bitácora sólo lo que cambió.
 */
export async function guardarConfiguracion(
  pedir: Pedir,
  datos: {
    razonSocial: string; moneda: string; idioma: 'es' | 'en';
    /** Vacía o nula es «nadie la declaró», y borrarla tiene que poder hacerse. */
    tasaDeRenta?: string | null;
  },
): Promise<void> {
  const r = await pedirSeguro(pedir, '/api/configuracion', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(datos),
  });
  await leer(r);
}

/**
 * Corregir la identificación de la empresa.
 *
 * ▶ Se manda el tipo y el número TAL COMO se escribió. La forma canónica la
 * deriva la base —disparador `identificacion_canonica`, migración `0070`— y
 * vuelve en la respuesta. Calcularla acá sería el segundo normalizador, y una
 * identificación normalizada de dos maneras es un contribuyente duplicado.
 *
 * `canonica` en `null` NO es un fallo: hay identificaciones que no tienen forma
 * canónica —las extranjeras— y números que no cuadran. La marca de provisional
 * que viene con el siguiente GET dice cuál de las dos cosas pasó.
 */
export async function guardarIdentificacion(
  pedir: Pedir, tipo: string, cruda: string,
): Promise<{ canonica: string | null }> {
  const r = await pedirSeguro(pedir, '/api/configuracion', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ tipo, cruda }),
  });
  return leer<{ canonica: string | null }>(r);
}

// ═══════════════════════════════════════════════════════════════════════════
// Contrapartes · clientes y proveedores (0080)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Una contraparte, con su ficha y su movimiento.
 *
 * ▶ `codigo` es el valor que viaja dentro de la clasificación de cada
 * movimiento suyo, y por eso no se edita: cambiarlo dejaría sus asientos
 * apuntando a nadie.
 *
 * ▶ `saldo` viaja como TEXTO — contrato §6.1. Y `sinNombrar` es el trabajo que
 * falta a la vista: una contraparte cuyo nombre es igual a su código es una que
 * la migración trajo de lo ya registrado y que nadie ha nombrado todavía.
 */
export interface Contraparte {
  readonly codigo: string;
  readonly nombre: string;
  readonly es_cliente: boolean;
  readonly es_proveedor: boolean;
  readonly tipo_identificacion: string | null;
  readonly identificacion_cruda: string | null;
  readonly identificacion: string | null;
  readonly dias_de_credito: number | null;
  readonly moneda: string | null;
  /**
   * Las DOS cuentas de la ficha (0084).
   *
   * ▶ Son dos porque a alguien se le puede comprar Y vender: la `0080` ya lo
   * había decidido para los papeles —dos banderas y no un `tipo`— y se le
   * escapó para la cuenta. Con una sola, la ficha de quien te imprime y te
   * compra software tendría que valer `5110` y `4120` a la vez.
   */
  readonly cuenta_de_compra: string | null;
  readonly cuenta_de_venta: string | null;
  readonly retencion: string | null;
  /** El correo de contacto. `null` = nadie lo declaró. */
  readonly correo: string | null;
  readonly activa: boolean;
  readonly nota: string | null;
  readonly sin_nombrar: boolean;
  readonly movimientos: number;
  /** Siempre POSITIVO. El lado lo dice `a_favor` — contrato §6.1: acá no se resta. */
  readonly saldo: string;
  readonly a_favor: boolean;
}

/** Lo que la pantalla manda para dar de alta o editar. */
export interface FichaDeContraparte {
  readonly nombre: string;
  readonly esCliente: boolean;
  readonly esProveedor: boolean;
  readonly codigo?: string | null;
  readonly tipoIdentificacion?: string | null;
  readonly identificacion?: string | null;
  readonly diasDeCredito?: number | null;
  readonly moneda?: string | null;
  readonly cuentaDeCompra?: string | null;
  /** Se guarda en minúsculas y sin espacios, y la base comprueba su forma
   *  (`SB183`). Vacío entra como `null`, que es «nadie lo declaró». */
  readonly correo?: string | null;
  readonly cuentaDeVenta?: string | null;
  readonly nota?: string | null;
}

export async function traerContrapartes(
  pedir: Pedir, papel?: string | null, texto?: string | null,
): Promise<Contraparte[]> {
  const q = new URLSearchParams();
  if (papel) q.set('papel', papel);
  if (texto) q.set('texto', texto);
  const cola = q.toString();
  const r = await pedirSeguro(pedir, `/api/contrapartes${cola ? `?${cola}` : ''}`);
  const cuerpo = await leer<{ contrapartes: Contraparte[] }>(r);
  return cuerpo.contrapartes;
}

/** Devuelve el código que la BASE derivó del nombre, no uno calculado acá. */
export async function agregarContraparte(
  pedir: Pedir, ficha: FichaDeContraparte,
): Promise<string> {
  const r = await pedirSeguro(pedir, '/api/contrapartes', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(ficha),
  });
  const cuerpo = await leer<{ codigo: string }>(r);
  return cuerpo.codigo;
}

export async function editarContraparte(
  pedir: Pedir, codigo: string, ficha: FichaDeContraparte,
): Promise<void> {
  await pedirSeguro(pedir, '/api/contrapartes', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...ficha, codigo }),
  });
}

export async function activarContraparte(
  pedir: Pedir, codigo: string, activa: boolean,
): Promise<void> {
  await pedirSeguro(pedir, '/api/contrapartes', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ codigo, activa }),
  });
}


// ═══════════════════════════════════════════════════════════════════════════
// Documentos · las facturas por pagar y su asiento (0081)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Una factura, con lo que dice el papel y el asiento que generó.
 *
 * ▶ Todos los importes viajan como TEXTO — contrato §6.1 — y las fechas
 * también: un `date` que cruza como instante se corre un día para atrás en
 * Costa Rica, que es UTC−6.
 *
 * ▶ `vencida` y `diasVencida` los calcula la BASE. Una resta de fechas en el
 * navegador se hace en la zona de quien mira, y quien mira puede estar en otra.
 * Es la misma decisión que `a_favor` en contrapartes.
 */
export interface Documento {
  readonly id: string;
  readonly tipo: string;
  readonly contraparte: string;
  readonly contraparte_nombre: string;
  readonly numero: string;
  readonly fecha: string;
  readonly vence: string | null;
  readonly vencida: boolean;
  readonly dias_vencida: number | null;
  readonly moneda: string;
  readonly subtotal: string;
  readonly impuesto: string;
  readonly total: string;
  readonly asiento: string;
  readonly glosa: string | null;
  /**
   * El total en la moneda de los LIBROS (`0120`). `total` está en la del PAPEL.
   *
   * ▶ Existe porque el pie sumaba `total` de todos los documentos y rotulaba el
   * resultado en colones: **sumaba dólares con colones**. `null` es «no hay con qué
   * convertirlo», y entonces el documento no está vivo — así que nunca entra al pie.
   */
  readonly total_local: string | null;
  /**
   * En la moneda de los LIBROS, no en la del papel (0082).
   *
   * ▶ En una factura en otra moneda `total` y `abierto` NO son el mismo número
   * ni lo pueden ser: el papel dice $113.02 y los libros dicen lo que valían
   * ese día. Son dos hechos distintos y por eso viajan los dos.
   */
  /**
   * La referencia fiscal de una nota: contra qué factura va, y por qué (0085).
   *
   * ▶ En una factura son `null`. No es que falte el dato: es que una factura no
   * va contra nada.
   */
  readonly ref_documento: string | null;
  readonly ref_numero: string | null;
  readonly ref_fecha: string | null;
  readonly ref_motivo: string | null;
  readonly aplicado: string;
  readonly abierto: string;
  /**
   * Si le queda algo vivo. Lo decide la BASE y no esta capa comparando
   * `abierto` contra cero: el dinero viaja como texto y no toca la aritmética
   * del navegador. Misma decisión que `a_favor` en las contrapartes.
   */
  readonly vivo: boolean;
}

/** Lo que la pantalla manda para registrar una factura de compra. */
export interface FacturaDeCompra {
  readonly contraparte: string;
  readonly numero: string;
  readonly fecha: string;
  readonly vence?: string | null;
  readonly moneda?: string | null;
  /** Texto, siempre. Un número aquí ya perdió lo que iba a perder. */
  readonly subtotal: string;
  readonly impuesto?: string | null;
  readonly cuenta?: string | null;
  readonly glosa?: string | null;
  readonly clasificacion?: Readonly<Record<string, string>>;
}

/**
 * El pie de la tabla de documentos.
 *
 * ▶▶ VIENE DE LA BASE Y NO SE SUMA ACÁ, y eso no es una preferencia: sumar
 * `total` y `abierto` fila por fila en JavaScript pasa cada importe por un
 * `Number`, y `"1250000.10"` deja de ser 1250000.10. Es lo que el guardián 3
 * prohíbe, y el error no se ve hasta la suma de la factura cien.
 *
 * ▶ Y suma sobre TODO lo que cumple el filtro, no sobre las filas que llegaron:
 * la lista viene recortada, y un pie que sumara lo recibido daría un número
 * correcto contestando una pregunta que nadie hizo.
 */
export interface TotalesDeDocumentos {
  readonly cuantos: number;
  readonly vencidos: number;
  readonly total: string;
  readonly abierto: string;
  readonly moneda: string;
}

export async function traerDocumentos(
  pedir: Pedir,
  filtro: { papel?: 'pagar' | 'cobrar' | null; contraparte?: string | null;
    anio?: number | null; mes?: number | null } = {},
): Promise<{ documentos: Documento[]; totales: TotalesDeDocumentos | null }> {
  const q = new URLSearchParams();
  // ▶ Por PAPEL y no por tipo: una nota de crédito no tiene lado propio, lo
  // tiene la factura que referencia. Ver la `0085`.
  if (filtro.papel) q.set('papel', filtro.papel);
  if (filtro.contraparte) q.set('contraparte', filtro.contraparte);
  if (filtro.anio) q.set('anio', String(filtro.anio));
  if (filtro.mes) q.set('mes', String(filtro.mes));
  const cola = q.toString();
  const r = await pedirSeguro(pedir, `/api/documentos${cola ? `?${cola}` : ''}`);
  /* ▶ Devuelve las dos cosas, y no sólo las filas.
     El pie viene de la MISMA transacción que la lista: dos peticiones separadas
     podrían contestar sobre estados distintos —alguien registra un pago entre
     una y otra— y el total dejaría de cuadrar con la tabla que tiene encima,
     sin que nada fallara. */
  const cuerpo = await leer<{
    documentos: Documento[]; totales: TotalesDeDocumentos | null;
  }>(r);
  return { documentos: cuerpo.documentos, totales: cuerpo.totales ?? null };
}

/**
 * Devuelve el número del ASIENTO, que es lo que quien registra puede ir a
 * buscar en el diario. El id del documento no significa nada para nadie.
 */
export async function registrarFacturaDeCompra(
  pedir: Pedir, factura: FacturaDeCompra,
): Promise<string> {
  const r = await pedirSeguro(pedir, '/api/documentos', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(factura),
  });
  const cuerpo = await leer<{ asiento: string }>(r);
  return cuerpo.asiento;
}

// ═══════════════════════════════════════════════════════════════════════════
// Pagos y aplicación (0082)
// ═══════════════════════════════════════════════════════════════════════════

/** Una aplicación: cuánto de este abono canceló este documento. */
export interface Aplicacion {
  /** `null` en lo COLOCADO: no hay fila escrita que llevar id. */
  readonly id: string | null;
  readonly fecha: string;
  readonly asiento: string;
  readonly linea: number;
  readonly glosa: string;
  readonly detalle: string | null;
  /**
   * Puede ser NEGATIVO, y no es un error: deshacer no borra, escribe la fila
   * contraria con su motivo. Las dos se enseñan.
   */
  readonly importe: string;
  readonly motivo: string | null;
  /**
   * Si este par ya está revertido. Lo dice la BASE porque saberlo acá pediría
   * sumar importes, y el dinero no toca la aritmética del navegador.
   */
  readonly deshecha: boolean;
  /**
   * Si alguien la declaró, o la colocó el informe por antigüedad (`0121`).
   *
   * ▶ Un pago que nadie repartió NO deja fila en `aplicaciones` —y no debe
   * dejarla: afirmaría una decisión que nadie tomó— pero la antigüedad sí lo
   * coloca. Hasta la `0121` la pantalla no lo decía, así que una factura salía
   * «saldada» con el detalle vacío: el saldo contestado y el «¿con qué pago?» no.
   *
   * `false` no lleva `id`, no lleva motivo y no se puede deshacer: no hay nada
   * escrito que borrar.
   */
  readonly declarada: boolean;
}

/**
 * A qué documento va este pago, y cuánto.
 *
 * ▶ El id del documento viaja como TEXTO, igual que el importe, y no es por
 * simetría: es un `bigint` de Postgres. Convertirlo a `number` es la misma
 * trampa que costó `enteroDeJson` en la API —un bigint no cabe en un número de
 * JavaScript— y además hacía que el guardián 3 lo leyera como dinero. La base
 * lo castea a `bigint` reciba texto o número, así que no hay nada que ganar.
 */
export interface Reparto {
  readonly documento: string;
  readonly importe: string;
}

export interface PagoNuevo {
  readonly papel: 'cobrar' | 'pagar';
  readonly contraparte: string;
  readonly fecha: string;
  /** De dónde sale la plata, o a dónde entra. */
  readonly cuenta: string;
  readonly moneda?: string | null;
  readonly importe: string;
  readonly aplicaciones?: readonly Reparto[];
  readonly glosa?: string | null;
  /** El número de cheque o de transferencia. No hay tabla de pagos: va acá. */
  readonly detalle?: string | null;
  /** ▶ De quién es la cuenta contraria, cuando NO es de efectivo (`0115`). El que
   *  pagó de su bolsillo queda como acreedor **con nombre**: sin esto el pasivo es
   *  correcto y anónimo, y uno anónimo no envejece ni se puede reintegrar. `null`
   *  es el caso normal — sale plata de un banco, y un banco no tiene acreedor. */
  readonly duenoDeLaCuenta?: string | null;
}

/** Devuelve el número del ASIENTO, que es lo que se puede ir a buscar. */
export async function registrarPago(pedir: Pedir, pago: PagoNuevo): Promise<string> {
  const r = await pedirSeguro(pedir, '/api/pagos', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(pago),
  });
  const cuerpo = await leer<{ asiento: string }>(r);
  return cuerpo.asiento;
}

/** Un cobro de una factura en moneda extranjera con un depósito en colones. */
export interface CobroEnMoneda {
  readonly contraparte: string;
  readonly fecha: string;
  /** La cuenta de banco, en moneda local, que recibió el depósito. */
  readonly cuenta: string;
  /** Los colones realmente recibidos, como texto. */
  readonly recibido: string;
  /** Las facturas en moneda extranjera a cancelar, por id. */
  readonly documentos: readonly string[];
  /** Qué hacer con la diferencia. */
  readonly tratamiento: 'diferencial' | 'saldo_a_favor';
  readonly glosa?: string | null;
  readonly detalle?: string | null;
}

/**
 * Cobra una o varias facturas en moneda extranjera liquidadas con un depósito en
 * la moneda de los libros. La factura se cancela a su valor LLEVADO; la
 * diferencia va a diferencial cambiario o a un saldo a favor del cliente, según
 * `tratamiento`. Devuelve el número del asiento.
 */
export async function registrarCobroEnMoneda(
  pedir: Pedir, cobro: CobroEnMoneda,
): Promise<string> {
  const r = await pedirSeguro(pedir, '/api/pagos', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ accion: 'cobro_en_moneda', ...cobro }),
  });
  const cuerpo = await leer<{ asiento: string }>(r);
  return cuerpo.asiento;
}

/**
 * La factura de VENTA. Mismo cuerpo que la de compra, otra puerta.
 *
 * ▶ Son dos funciones y no una con un `tipo` porque la base son dos: la de
 * venta pone el `customer` en las dos líneas —toda cuenta de ingreso lo exige—
 * y la de compra no pone ninguna. Un parámetro acá escondería que lo que pasa
 * al otro lado es distinto.
 */
export async function registrarFacturaDeVenta(
  pedir: Pedir, factura: FacturaDeCompra,
): Promise<string> {
  const r = await pedirSeguro(pedir, '/api/documentos', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...factura, tipo: 'factura_cobrar' }),
  });
  const cuerpo = await leer<{ asiento: string }>(r);
  return cuerpo.asiento;
}

/** Lo que la pantalla manda para acreditar parte de una factura. */
export interface NotaNueva {
  /** Contra qué factura va. Obligatorio: sin esto es un abono que no dice qué cancela. */
  readonly documento: string;
  readonly numero: string;
  readonly fecha: string;
  readonly subtotal: string;
  readonly impuesto?: string | null;
  /** Por qué. Obligatorio, y es lo único que lo explica meses después. */
  readonly motivo: string;
  readonly cuenta?: string | null;
  readonly glosa?: string | null;
  readonly clasificacion?: Readonly<Record<string, string>>;
}

/**
 * Devuelve el número del ASIENTO.
 *
 * ▶ Ni la contraparte ni la moneda viajan acá: salen del documento que
 * referencia. Mandarlas sería un sitio donde teclear algo distinto de lo que
 * dice la factura que se está corrigiendo.
 */
export async function registrarNotaDeCredito(
  pedir: Pedir, nota: NotaNueva,
): Promise<string> {
  const r = await pedirSeguro(pedir, '/api/documentos', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...nota, tipo: 'nota_credito' }),
  });
  const cuerpo = await leer<{ asiento: string }>(r);
  return cuerpo.asiento;
}

/**
 * Una nota de débito: la que SUBE lo que se debe.
 *
 * ▶ Lleva `vence` y la de crédito no, y no es un descuido de simetría: una nota
 * de débito abre deuda, y una deuda tiene fecha de pago o no la tiene. Una de
 * crédito no abre nada.
 */
export interface NotaDebitoNueva extends NotaNueva {
  readonly vence?: string | null;
}

export async function registrarNotaDeDebito(
  pedir: Pedir, nota: NotaDebitoNueva,
): Promise<string> {
  const r = await pedirSeguro(pedir, '/api/documentos', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...nota, tipo: 'nota_debito' }),
  });
  const cuerpo = await leer<{ asiento: string }>(r);
  return cuerpo.asiento;
}

/**
 * Una línea que abona y todavía tiene algo sin repartir.
 *
 * ▶ Es lo que `sin_aplicar` cuenta en el corte, línea por línea para poder
 * elegir una. Sale de los MOVIMIENTOS y no de una tabla de pagos, por lo mismo
 * que no hay tabla de pagos: un anticipo, una compensación o una nota de
 * crédito que no cupo son abonos igual de legítimos y ninguno tiene ficha.
 */
export interface AbonoSuelto {
  readonly asiento: string;
  readonly linea: number;
  readonly fecha: string;
  readonly glosa: string;
  readonly detalle: string | null;
  readonly cuenta: string;
  readonly contraparte: string | null;
  readonly importe: string;
  readonly aplicado: string;
  readonly queda: string;
}

export async function traerAbonosSueltos(
  pedir: Pedir, papel: 'pagar' | 'cobrar', contraparte?: string | null,
): Promise<AbonoSuelto[]> {
  const q = new URLSearchParams({ papel });
  if (contraparte) q.set('contraparte', contraparte);
  const r = await pedirSeguro(pedir, `/api/aplicaciones?${q.toString()}`);
  const cuerpo = await leer<{ sueltos: AbonoSuelto[] }>(r);
  return cuerpo.sueltos;
}

/**
 * Aplicar un abono YA asentado.
 *
 * ▶ Volvió. La `0082` la había dejado y el guardián 10 la quitó porque ninguna
 * pantalla la llamaba: «una ruta viva que nadie alcanza es una promesa que nadie
 * ha cobrado». Entra otra vez con su lista y con su pantalla, no «por si acaso».
 */
export async function aplicarAbono(
  pedir: Pedir, asiento: string, linea: number, repartos: readonly Reparto[],
): Promise<void> {
  await pedirSeguro(pedir, '/api/aplicaciones', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ asiento, linea, aplicaciones: repartos }),
  });
}

export async function traerAplicaciones(
  pedir: Pedir, documento: number,
): Promise<Aplicacion[]> {
  const r = await pedirSeguro(pedir, `/api/aplicaciones?documento=${documento}`);
  const cuerpo = await leer<{ aplicaciones: Aplicacion[] }>(r);
  return cuerpo.aplicaciones;
}

/**
 * Deshacer NO borra: escribe la fila contraria con su motivo, y las dos quedan.
 * Por eso el motivo es obligatorio y por eso esto no es un `DELETE`.
 */
export async function desaplicar(
  pedir: Pedir, id: number, motivo: string,
): Promise<void> {
  await pedirSeguro(pedir, '/api/aplicaciones', {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ id, motivo }),
  });
}
